import React, { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './App.css';
import { Button } from './components/ui/button';
import { initMercadoPago, Wallet, CardPayment } from '@mercadopago/sdk-react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './components/ui/card';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Textarea } from './components/ui/textarea';
import { Badge } from './components/ui/badge';
import { Separator } from './components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from './components/ui/dialog';
import { useToast } from './hooks/use-toast';
import { Toaster } from './components/ui/toaster';
import { ShoppingCart, Package, Users, Settings, Plus, Minus, Trash2, Heart, Star, ArrowLeft, Truck, Scale, Mail, MessageCircle, Instagram, Filter, Edit, ChevronLeft, ChevronRight, Search, X, Barcode, PackagePlus } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API = `${BACKEND_URL}/api`;

// Helper function to get correct image URL (handles both local and Cloudinary URLs)
const getImageUrl = (url) => {
  if (!url) return '';
  // If URL is already absolute (Cloudinary or other external), return as-is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  // Otherwise, prepend backend URL for local images
  return `${BACKEND_URL}${url}`;
};

// ============================================
// 🛒 MODO CATÁLOGO - Cambiar a false para habilitar ventas
// ============================================
const CATALOG_MODE = false; // true = solo catálogo, false = tienda completa
// ============================================

// ============================================
// 💼 MODO MAYOREO - Cambiar a true para habilitar mayoreo
// ============================================
const WHOLESALE_ENABLED = false; // true = mayoreo activo, false = mayoreo desactivado
// ============================================

// Authentication Context
const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Auth Provider Component
const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in
    const token = localStorage.getItem('adminToken');
    if (token) {
      verifyToken(token);
    } else {
      setLoading(false);
    }
  }, []);

  const verifyToken = async (token) => {
    try {
      const response = await axios.post(`${API}/auth/verify`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.valid) {
        setIsAuthenticated(true);
        setUser({ username: response.data.username });
        // Set default authorization header for all future requests
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      } else {
        localStorage.removeItem('adminToken');
      }
    } catch (error) {
      console.error('Token verification failed:', error);
      localStorage.removeItem('adminToken');
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, {
        username,
        password
      });

      if (response.data.success) {
        const token = response.data.token;
        localStorage.setItem('adminToken', token);
        setIsAuthenticated(true);
        setUser({ username });
        // Set default authorization header
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        return { success: true };
      } else {
        return { success: false, message: response.data.message };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        message: error.response?.data?.detail || 'Error de conexión' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('adminToken');
    delete axios.defaults.headers.common['Authorization'];
    setIsAuthenticated(false);
    setUser(null);
  };

  const value = {
    isAuthenticated,
    user,
    loading,
    login,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Login Component
const LoginPage = () => {
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    const result = await login(credentials.username, credentials.password);
    
    if (result.success) {
      navigate('/admin');
    } else {
      setError(result.message);
    }
    setIsLoading(false);
  };

  const handleChange = (e) => {
    setCredentials({
      ...credentials,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-cyan-50 to-pink-50">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <div className="flex justify-center mb-4">
            <img 
              src="/api/uploads/logo.png" 
              alt="DI Candy Rush" 
              className="h-16 w-auto"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>
          <h2 className="text-2xl font-bold text-center">Acceso de Administrador</h2>
          <p className="text-center text-gray-600">
            Ingresa tus credenciales para acceder al panel de administración
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="username">Usuario</Label>
              <Input
                id="username"
                name="username"
                type="text"
                value={credentials.username}
                onChange={handleChange}
                required
                placeholder="Ingresa tu usuario"
                data-testid="username-input"
              />
            </div>
            <div>
              <Label htmlFor="password">Contraseña</Label>
              <Input
                id="password"
                name="password"
                type="password"
                value={credentials.password}
                onChange={handleChange}
                required
                placeholder="Ingresa tu contraseña"
                data-testid="password-input"
              />
            </div>
            {error && (
              <div className="text-red-600 text-sm text-center bg-red-50 p-2 rounded">
                {error}
              </div>
            )}
            <Button
              type="submit"
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-cyan-500 to-pink-500 hover:from-cyan-600 hover:to-pink-600"
              data-testid="login-submit"
            >
              {isLoading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
            </Button>
          </form>
          <div className="mt-6 text-center">
            <Link to="/" className="text-sm text-cyan-600 hover:text-cyan-800">
              ← Volver a la tienda
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/admin/login');
    }
  }, [isAuthenticated, loading, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-cyan-500 mx-auto mb-4"></div>
          <p>Verificando acceso...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null; // Will redirect in useEffect
  }

  return children;
};

// Top Seller Badge Component
const TopSellerBadge = () => {
  return (
    <div className="absolute top-2 right-2 z-20">
      <div className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-2 py-1 rounded-full text-xs font-bold shadow-lg flex items-center space-x-1">
        <span>👑</span>
        <span>TOP VENTAS</span>
      </div>
    </div>
  );
};

// Product Card Component with Image Gallery
const ProductCard = ({ product, addToCart, addingToCart }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [timeLeft, setTimeLeft] = useState('');
  
  // Get all images - use image_urls if available, otherwise fallback to image_url
  const images = product.image_urls && product.image_urls.length > 0 
    ? product.image_urls 
    : (product.image_url ? [product.image_url] : []);
  
  // Check if discount is active
  const hasActiveDiscount = product.discount_percentage > 0 && 
    product.discount_ends_at && 
    new Date(product.discount_ends_at) > new Date();
  
  // Calculate discounted price
  const originalPrice = parseFloat(product.price);
  const discountedPrice = hasActiveDiscount 
    ? originalPrice * (1 - product.discount_percentage / 100) 
    : originalPrice;
  
  // Countdown timer effect
  useEffect(() => {
    if (!hasActiveDiscount) {
      setTimeLeft('');
      return;
    }
    
    const updateTimer = () => {
      const now = new Date();
      const end = new Date(product.discount_ends_at);
      const diff = end - now;
      
      if (diff <= 0) {
        setTimeLeft('');
        return;
      }
      
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      if (days > 0) {
        setTimeLeft(`${days}d ${hours}h ${minutes}m`);
      } else if (hours > 0) {
        setTimeLeft(`${hours}h ${minutes}m ${seconds}s`);
      } else {
        setTimeLeft(`${minutes}m ${seconds}s`);
      }
    };
    
    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [hasActiveDiscount, product.discount_ends_at]);
  
  const nextImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };
  
  const prevImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };
  
  return (
    <Card className={`group hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 bg-white border-2 border-transparent hover:border-cyan-200 relative ${hasActiveDiscount ? 'ring-2 ring-orange-400' : ''}`}>
      {product.is_top_seller && <TopSellerBadge />}
      
      <CardHeader className="p-0">
        <div className="relative overflow-hidden rounded-t-lg">
          <img 
            src={getImageUrl(images[currentImageIndex])}
            alt={product.name}
            className="w-full h-48 object-contain group-hover:scale-110 transition-transform duration-300"
            data-testid={`product-image-${product.id}`}
          />
          <Badge className="absolute top-3 left-3 bg-cyan-500 text-white">
            {product.category}
          </Badge>
          
          {/* Discount Badge - Bottom left */}
          {hasActiveDiscount && (
            <div className="absolute bottom-3 left-3 z-30 bg-gradient-to-r from-orange-500 to-red-500 text-white px-3 py-1 rounded-full text-sm font-bold shadow-lg animate-pulse">
              -{product.discount_percentage}% OFF
            </div>
          )}
          
          {/* Image navigation for multiple images */}
          {images.length > 1 && (
            <>
              {/* Navigation arrows */}
              <button
                onClick={prevImage}
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-1 shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <ChevronLeft className="w-5 h-5 text-gray-700" />
              </button>
              <button
                onClick={nextImage}
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-1 shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <ChevronRight className="w-5 h-5 text-gray-700" />
              </button>
              
              {/* Image dots indicator */}
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex space-x-1">
                {images.map((imgUrl, index) => (
                  <button
                    key={`${imgUrl}-${index}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentImageIndex(index);
                    }}
                    className={`w-2 h-2 rounded-full transition-all ${
                      index === currentImageIndex 
                        ? 'bg-pink-500 w-4' 
                        : 'bg-white/70 hover:bg-white'
                    }`}
                  />
                ))}
              </div>
              
              {/* Image counter */}
              <div className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-0.5 rounded-full">
                {currentImageIndex + 1}/{images.length}
              </div>
            </>
          )}
        </div>
      </CardHeader>
      
      <CardContent className="p-6">
        <CardTitle className="text-lg font-bold text-gray-900 mb-2" data-testid={`product-name-${product.id}`}>
          {product.name}
        </CardTitle>
        
        {/* Descripción e Ingredientes en carrusel horizontal */}
        <div className="relative mb-3">
          <div className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide gap-2 pb-2" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            {/* Slide 1: Descripción */}
            <div className="flex-none w-full snap-center">
              <div className="bg-gray-50 rounded-lg p-3 h-[120px] flex flex-col">
                <p className="text-xs font-semibold text-gray-500 mb-1 flex-shrink-0">📝 Descripción</p>
                <p className="text-sm text-gray-600 overflow-y-auto pr-1 leading-snug" style={{ scrollbarWidth: 'thin' }}>{product.description}</p>
              </div>
            </div>
            
            {/* Slide 2: Ingredientes (solo si existen) */}
            {product.ingredients && (
              <div className="flex-none w-full snap-center">
                <div className="bg-orange-50 rounded-lg p-3 h-[120px] flex flex-col">
                  <p className="text-xs font-semibold text-orange-600 mb-1 flex-shrink-0">📋 Ingredientes</p>
                  <p className="text-xs text-gray-600 leading-relaxed overflow-y-auto pr-1" style={{ scrollbarWidth: 'thin' }}>{product.ingredients}</p>
                </div>
              </div>
            )}
          </div>
          
          {/* Indicador de scroll (solo si hay ingredientes) */}
          {product.ingredients && (
            <div className="flex justify-center gap-1 mt-1">
              <span className="text-xs text-gray-400">← Desliza para ver ingredientes →</span>
            </div>
          )}
        </div>
        
        {/* Weight and dimensions info */}
        <div className="flex items-center space-x-4 mb-3 text-sm text-gray-500">
          {product.weight_display > 0 && (
            <div className="flex items-center">
              <Scale className="w-3 h-3 mr-1" />
              {product.weight_display}{product.weight_unit || 'g'}
            </div>
          )}
          {product.dimensions && (
            <div className="flex items-center">
              <Package className="w-3 h-3 mr-1" />
              {product.dimensions}
            </div>
          )}
        </div>
        
        {/* Price section with discount */}
        <div className="mb-4">
          {hasActiveDiscount ? (
            <div>
              <div className="flex items-center space-x-2">
                <span className="text-lg text-gray-400 line-through">
                  ${originalPrice.toFixed(2)}
                </span>
                <span className="text-2xl font-bold text-orange-600" data-testid={`product-price-${product.id}`}>
                  ${discountedPrice.toFixed(2)} MXN
                </span>
              </div>
              {timeLeft && (
                <div className="flex items-center mt-1 text-sm text-orange-600 bg-orange-50 px-2 py-1 rounded-full w-fit">
                  <span className="mr-1">⏰</span>
                  <span>Termina en: {timeLeft}</span>
                </div>
              )}
            </div>
          ) : (
            <span className="text-2xl font-bold text-pink-600" data-testid={`product-price-${product.id}`}>
              ${originalPrice.toFixed(2)} MXN
            </span>
          )}
          <div className="mt-2">
            <Badge variant={product.stock > 0 ? "secondary" : "destructive"}>
              {product.stock > 0 ? `${product.stock} disponibles` : 'Agotado'}
            </Badge>
          </div>
        </div>
      </CardContent>
      
      {/* Ocultar botón en modo catálogo */}
      {!CATALOG_MODE && (
        <CardFooter className="p-6 pt-0">
          <Button 
            onClick={() => addToCart({
              ...product, 
              currentPrice: discountedPrice, 
              hasDiscount: hasActiveDiscount,
              discount_percentage: hasActiveDiscount ? product.discount_percentage : 0
            })}
            disabled={product.stock <= 0 || addingToCart === product.id}
            className={`w-full bg-gradient-to-r from-cyan-500 to-pink-500 hover:from-cyan-600 hover:to-pink-600 text-white font-semibold rounded-lg shadow-md transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
              addingToCart === product.id ? 'animate-pulse bg-green-500' : ''
            }`}
            data-testid={`add-to-cart-${product.id}`}
          >
            {addingToCart === product.id ? (
              <>
                <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ¡Agregado!
              </>
            ) : (
              <>
                <ShoppingCart className="w-4 h-4 mr-2" />
                {product.stock > 0 ? 'Agregar al Carrito' : 'Agotado'}
              </>
            )}
          </Button>
        </CardFooter>
      )}
    </Card>
  );
};

// Homepage Component
const Homepage = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [cart, setCart] = useState([]);
  const [cartBounce, setCartBounce] = useState(false);
  const [addingToCart, setAddingToCart] = useState(null); // Store product ID being added
  const [currentPage, setCurrentPage] = useState(1);
  const PRODUCTS_PER_PAGE = 24;
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    loadCart();
  }, []);

  useEffect(() => {
    fetchProducts();
    setCurrentPage(1); // reset page when category changes
  }, [selectedCategory]);

  // Reset to first page whenever search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  // Debounced product search
  useEffect(() => {
    const trimmed = searchQuery.trim();
    if (!trimmed) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    const timeoutId = setTimeout(async () => {
      try {
        const response = await axios.get(`${API}/products/search`, {
          params: { q: trimmed, limit: 24 }
        });
        setSearchResults(response.data);
      } catch (error) {
        console.error('Error searching products:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 350);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data.categories);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      // Always fetch all products with reviews. If a category is selected,
      // filter by that category. Otherwise return every product available.
      const url = selectedCategory 
        ? `${API}/products/with-reviews?category=${selectedCategory}` 
        : `${API}/products/with-reviews`;
      
      const response = await axios.get(url);
      setProducts(response.data);
      console.log(selectedCategory ? `📂 Category products loaded: ${response.data.length}` : `🛍️ All products loaded: ${response.data.length}`);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const loadCart = () => {
    const savedCart = localStorage.getItem('diCandyCart');
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    }
  };

  const addToCart = (product) => {
    // Verificar que hay stock disponible (incluyendo stock negativo)
    if (product.stock <= 0) {
      toast({
        title: "Producto agotado",
        description: "Lo sentimos, este producto está agotado.",
        variant: "destructive",
      });
      return;
    }

    const existingItem = cart.find(item => item.product_id === product.id);
    let newCart;
    
    if (existingItem) {
      // Verificar que no se exceda el stock disponible
      if (existingItem.quantity >= product.stock) {
        toast({
          title: "Stock limitado",
          description: `No puedes agregar más de ${product.stock} unidades de este producto (stock disponible).`,
          variant: "destructive",
        });
        return;
      }
      
      newCart = cart.map(item =>
        item.product_id === product.id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      );
    } else {
      // Use discounted price if available, otherwise use regular price
      const priceToUse = product.currentPrice || product.price;
      
      newCart = [...cart, {
        product_id: product.id,
        name: product.name,
        price: priceToUse,  // Use current price (may include discount)
        original_price: product.price,  // Keep original price for reference
        wholesale_price: product.wholesale_price || null,
        has_discount: product.hasDiscount || false,
        discount_percentage: product.discount_percentage || 0,
        image_url: product.image_url,
        weight_kg: product.weight_kg || 0,
        weight_display: product.weight_display || 0,
        weight_unit: product.weight_unit || 'g',
        quantity: 1
      }];
    }
    
    setCart(newCart);
    localStorage.setItem('diCandyCart', JSON.stringify(newCart));

    // Visual feedback
    setAddingToCart(product.id);
    setCartBounce(true);

    // Show success toast
    const discountText = product.hasDiscount ? ` (con ${product.discount_percentage}% de descuento)` : '';
    toast({
      title: "¡Producto agregado!",
      description: `${product.name} se agregó a tu carrito${discountText}`,
      className: "bg-green-50 border-green-200",
    });

    // Reset visual effects
    setTimeout(() => {
      setAddingToCart(null);
      setCartBounce(false);
    }, 1000);
  };

  const cartItemsCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-pink-50 to-purple-50">
      {/* Header */}
      <header className="bg-white shadow-lg border-b-4 border-gradient-candy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_american-treats/artifacts/fa38658k_JUNTOS-2-PNG.png"
                alt="DI Candy Rush Logo"
                className="h-16 w-auto logo-glow"
              />
              <div className="hidden md:flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-cyan-50 to-pink-50 border border-cyan-200">
                <span className="text-sm font-semibold bg-gradient-to-r from-cyan-600 to-pink-600 bg-clip-text text-transparent">
                  ✈️ Importado desde USA
                </span>
              </div>
            </div>
            
            <nav className="flex items-center space-x-2 md:space-x-6 text-xs md:text-base">
              {/* Admin link removed for security - access only via direct URL /admin/login */}
              
              {/* WhatsApp Button */}
              <Button 
                onClick={() => window.open('https://wa.me/525574918395?text=Hola!%20Tengo%20una%20pregunta%20sobre%20los%20productos%20de%20DI%20Candy%20Rush', '_blank')}
                variant="outline" 
                size="sm"
                className="border-green-500 text-green-600 hover:bg-green-50 text-xs md:text-sm px-2 md:px-4"
                data-testid="whatsapp-button"
              >
                <MessageCircle className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
                <span className="hidden md:inline">WhatsApp</span>
                <span className="md:hidden">WA</span>
              </Button>
              
              {/* Instagram Button */}
              <Button 
                onClick={() => window.open('https://www.instagram.com/dicandyrush?igsh=MTZ3bHRsNThtODI1Zw==', '_blank')}
                variant="outline" 
                size="sm"
                className="border-purple-500 text-purple-600 hover:bg-purple-50 text-xs md:text-sm px-2 md:px-4"
                data-testid="instagram-button"
              >
                <Instagram className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
                <span className="hidden md:inline">Instagram</span>
                <span className="md:hidden">IG</span>
              </Button>
              
              {/* Ocultar carrito en modo catálogo */}
              {!CATALOG_MODE && (
                <Button 
                  onClick={() => navigate('/cart')}
                  variant="outline"
                  size="sm" 
                  className={`relative border-cyan-400 text-cyan-600 hover:bg-cyan-50 text-xs md:text-sm px-2 md:px-4 transition-all duration-300 ${
                    cartBounce ? 'animate-bounce' : ''
                  }`}
                  data-testid="cart-button"
                >
                  <ShoppingCart className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
                  <span className="hidden md:inline">Carrito</span>
                  <span className="md:hidden">🛒</span>
                  {cartItemsCount > 0 && (
                    <Badge className={`absolute -top-2 -right-2 bg-pink-500 text-white text-xs px-2 py-1 rounded-full min-w-[20px] h-5 flex items-center justify-center transition-all duration-300 ${
                      cartBounce ? 'animate-pulse scale-110' : ''
                    }`}>
                      {cartItemsCount}
                    </Badge>
                  )}
                </Button>
              )}
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold mb-6 logo-typography" data-testid="main-title">
              DI Candy Rush: Un rush de sabor.
            </h1>
            <p className="text-xl text-gray-600 mb-4">
              Los dulces más buscados, importados directamente y listos para enviar.
            </p>
            {/* Botón de compra - Oculto en modo catálogo */}
            {!CATALOG_MODE && (
              <Button 
                size="lg"
                className="bg-gradient-to-r from-cyan-500 to-pink-500 hover:from-cyan-600 hover:to-pink-600 text-white font-semibold px-6 py-4 text-lg rounded-full shadow-lg transform hover:scale-105 transition-all duration-200 flex items-center gap-3 mx-auto"
                onClick={() => document.getElementById('products').scrollIntoView({ behavior: 'smooth' })}
                data-testid="shop-now-button"
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_american-treats/artifacts/fa38658k_JUNTOS-2-PNG.png"
                  alt="DI Candy Rush"
                  className="h-8 w-auto"
                />
                <span>¡Compra Ahora!</span>
              </Button>
            )}
            {/* Botón alternativo en modo catálogo */}
            {CATALOG_MODE && (
              <Button 
                size="lg"
                className="bg-gradient-to-r from-cyan-500 to-pink-500 hover:from-cyan-600 hover:to-pink-600 text-white font-semibold px-6 py-4 text-lg rounded-full shadow-lg transform hover:scale-105 transition-all duration-200 flex items-center gap-3 mx-auto"
                onClick={() => document.getElementById('products').scrollIntoView({ behavior: 'smooth' })}
                data-testid="view-catalog-button"
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_american-treats/artifacts/fa38658k_JUNTOS-2-PNG.png"
                  alt="DI Candy Rush"
                  className="h-8 w-auto"
                />
                <span>¡Ver Catálogo!</span>
              </Button>
            )}
          </div>
        </div>
      </section>

      {/* Category Filter Section */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center space-y-4">
            {/* Search bar */}
            <div className="relative w-full max-w-xl" data-testid="product-search-container">
              <div className="relative p-[2px] rounded-full bg-gradient-to-r from-cyan-400 via-pink-400 to-cyan-400 shadow-lg">
                <div className="flex items-center bg-white rounded-full px-4 py-2">
                  <Search className="w-5 h-5 text-pink-500 mr-2 flex-shrink-0" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Buscar dulces, chocolates, marcas..."
                    className="flex-1 px-2 py-1 bg-transparent border-none focus:outline-none focus:ring-0 text-gray-700"
                    data-testid="product-search-input"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="ml-2 p-1 rounded-full hover:bg-gray-100 transition-colors"
                      aria-label="Limpiar búsqueda"
                      data-testid="product-search-clear"
                    >
                      <X className="w-4 h-4 text-gray-500" />
                    </button>
                  )}
                </div>
              </div>
              {searchQuery.trim() && (
                <p className="text-xs text-center text-gray-500 mt-2">
                  {isSearching ? 'Buscando...' : `${searchResults.length} resultado(s) para "${searchQuery}"`}
                </p>
              )}
            </div>

            {/* Filter container with gradient border */}
            <div className="relative p-[2px] rounded-full bg-gradient-to-r from-cyan-400 via-pink-400 to-cyan-400 shadow-lg">
              <div className="flex items-center bg-white rounded-full px-4 py-2 space-x-3">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-r from-cyan-500 to-pink-500">
                  <Filter className="w-4 h-4 text-white" />
                </div>
                <select 
                  value={selectedCategory} 
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-56 md:w-64 px-2 py-2 bg-transparent border-none focus:outline-none focus:ring-0 text-gray-700 font-medium cursor-pointer appearance-none"
                  style={{ backgroundImage: 'none' }}
                >
                  <option value="">🍬 Todas las categorías</option>
                  {categories.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
                <ChevronRight className="w-4 h-4 text-gray-400 rotate-90" />
              </div>
            </div>
            
            {selectedCategory && (
              <button 
                onClick={() => setSelectedCategory('')}
                className="flex items-center space-x-2 text-sm text-gray-500 hover:text-pink-600 transition-colors"
              >
                <span>✕</span>
                <span>Limpiar filtro</span>
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-4">
            {searchQuery.trim()
              ? `🔎 Resultados de búsqueda`
              : selectedCategory
                ? `🗂️ ${selectedCategory}`
                : '🍬 Todos nuestros productos 🍬'}
          </h2>
          {searchQuery.trim() ? (
            <p className="text-center text-lg text-gray-600 mb-8">
              Mostrando resultados para: <span className="font-semibold text-pink-600">"{searchQuery}"</span>
            </p>
          ) : selectedCategory ? (
            <p className="text-center text-lg text-gray-600 mb-8">
              Mostrando todos los productos de la categoría: <span className="font-semibold text-pink-600">{selectedCategory}</span>
            </p>
          ) : (
            <p className="text-center text-lg text-gray-600 mb-8">
              Explora <span className="font-semibold text-cyan-600">{products.length}</span> dulces y golosinas disponibles
            </p>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8" data-testid="products-grid">
            {(() => {
              const source = searchQuery.trim() ? searchResults : products;
              const start = (currentPage - 1) * PRODUCTS_PER_PAGE;
              const paginated = source.slice(start, start + PRODUCTS_PER_PAGE);
              return paginated.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  addToCart={addToCart}
                  addingToCart={addingToCart}
                />
              ));
            })()}
          </div>

          {/* Pagination controls */}
          {(() => {
            const source = searchQuery.trim() ? searchResults : products;
            const totalPages = Math.max(1, Math.ceil(source.length / PRODUCTS_PER_PAGE));
            if (totalPages <= 1) return null;

            const goTo = (n) => {
              const target = Math.min(totalPages, Math.max(1, n));
              setCurrentPage(target);
              // Scroll back to the products section on page change
              setTimeout(() => {
                const el = document.getElementById('products');
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }, 50);
            };

            // Build page numbers with ellipsis for large ranges
            const pages = [];
            const window_ = 1; // neighbors on each side
            for (let i = 1; i <= totalPages; i++) {
              if (i === 1 || i === totalPages || (i >= currentPage - window_ && i <= currentPage + window_)) {
                pages.push(i);
              } else if (pages[pages.length - 1] !== '…') {
                pages.push('…');
              }
            }

            return (
              <div className="mt-10 flex flex-col items-center gap-3" data-testid="pagination-controls">
                <div className="flex items-center gap-1 sm:gap-2 flex-wrap justify-center">
                  <button
                    onClick={() => goTo(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-3 py-2 rounded-lg text-sm font-medium bg-white border border-gray-200 text-gray-700 hover:border-pink-400 hover:text-pink-600 disabled:opacity-40 disabled:hover:border-gray-200 disabled:hover:text-gray-700 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                    data-testid="pagination-prev"
                  >
                    <ChevronLeft className="w-4 h-4" /> Anterior
                  </button>

                  {pages.map((p, idx) =>
                    p === '…' ? (
                      <span key={`e-${idx}`} className="px-2 text-gray-400 select-none">…</span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => goTo(p)}
                        aria-current={p === currentPage ? 'page' : undefined}
                        className={`min-w-[40px] px-3 py-2 rounded-lg text-sm font-semibold transition-all ${
                          p === currentPage
                            ? 'bg-gradient-to-r from-cyan-500 to-pink-500 text-white shadow-md scale-105'
                            : 'bg-white border border-gray-200 text-gray-700 hover:border-pink-400 hover:text-pink-600'
                        }`}
                        data-testid={`pagination-page-${p}`}
                      >
                        {p}
                      </button>
                    )
                  )}

                  <button
                    onClick={() => goTo(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-2 rounded-lg text-sm font-medium bg-white border border-gray-200 text-gray-700 hover:border-pink-400 hover:text-pink-600 disabled:opacity-40 disabled:hover:border-gray-200 disabled:hover:text-gray-700 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                    data-testid="pagination-next"
                  >
                    Siguiente <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-xs text-gray-500">
                  Página {currentPage} de {totalPages} · {source.length} producto{source.length === 1 ? '' : 's'} en total
                </p>
              </div>
            );
          })()}
          
          {searchQuery.trim() && !isSearching && searchResults.length === 0 && (
            <div className="text-center py-12" data-testid="no-search-results">
              <p className="text-gray-500 text-lg">
                No encontramos productos para <span className="font-semibold">"{searchQuery}"</span>
              </p>
              <button
                onClick={() => setSearchQuery('')}
                className="mt-4 text-pink-600 hover:text-pink-700 underline text-sm"
              >
                Limpiar búsqueda
              </button>
            </div>
          )}

          {!searchQuery.trim() && products.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">
                {selectedCategory ? `No hay productos en la categoría "${selectedCategory}"` : 'No hay productos disponibles'}
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Shipping Info Section */}
      <section className="py-16 bg-gradient-to-r from-cyan-100 to-pink-100">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h3 className="text-3xl font-bold text-gray-900 mb-8">📦 Información de Envío UPS</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="bg-white/80 backdrop-blur-sm">
              <CardContent className="p-6 text-center">
                <Truck className="w-12 h-12 text-cyan-600 mx-auto mb-4" />
                <h4 className="text-xl font-semibold mb-2">Hasta 3kg</h4>
                <p className="text-3xl font-bold text-cyan-600 mb-2">$175 MXN</p>
                <p className="text-gray-600">Envío UPS Standard</p>
              </CardContent>
            </Card>
            <Card className="bg-white/80 backdrop-blur-sm">
              <CardContent className="p-6 text-center">
                <Truck className="w-12 h-12 text-pink-600 mx-auto mb-4" />
                <h4 className="text-xl font-semibold mb-2">Hasta 5kg</h4>
                <p className="text-3xl font-bold text-pink-600 mb-2">$210 MXN</p>
                <p className="text-gray-600">Envío UPS Standard</p>
              </CardContent>
            </Card>
          </div>
          <p className="mt-6 text-gray-600">
            Tiempo de entrega: 3-5 días hábiles • Procesamos de lunes a viernes
          </p>
        </div>
      </section>

      {/* Contact and Social Section */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h3 className="text-3xl font-bold text-gray-900 mb-8">📱 ¡Conéctate con Nosotros!</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="hover:shadow-lg transition-shadow cursor-pointer" 
                  onClick={() => window.open('https://wa.me/525574918395?text=Hola!%20Tengo%20una%20pregunta%20sobre%20los%20productos%20de%20DI%20Candy%20Rush', '_blank')}>
              <CardContent className="p-6 text-center">
                <MessageCircle className="w-12 h-12 text-green-600 mx-auto mb-4" />
                <h4 className="text-xl font-semibold mb-2">WhatsApp</h4>
                <p className="text-gray-600 mb-2">¿Tienes preguntas? ¡Escríbenos!</p>
                <p className="text-green-600 font-semibold">+52 55 7491 8395</p>
              </CardContent>
            </Card>
            
            <Card className="hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => window.open('https://www.instagram.com/dicandyrush?igsh=MTZ3bHRsNThtODI1Zw==', '_blank')}>
              <CardContent className="p-6 text-center">
                <Instagram className="w-12 h-12 text-purple-600 mx-auto mb-4" />
                <h4 className="text-xl font-semibold mb-2">Instagram</h4>
                <p className="text-gray-600 mb-2">¡Síguenos para ofertas exclusivas!</p>
                <p className="text-purple-600 font-semibold">@dicandyrush</p>
              </CardContent>
            </Card>
          </div>
          
          {/* Aviso de ventas al mayoreo */}
          <div className="mt-8 p-4 bg-gradient-to-r from-purple-100 to-cyan-100 rounded-xl border border-purple-200">
            <p className="text-gray-700">
              <span className="font-semibold">💼 ¿Interesado en ventas al mayoreo?</span>
              <br />
              <span className="text-sm">Contáctanos por WhatsApp para información sobre precios especiales para tu negocio.</span>
            </p>
          </div>
        </div>
      </section>

      {/* Wholesale Section - Solo visible si WHOLESALE_ENABLED es true */}
      {WHOLESALE_ENABLED && (
        <section className="py-16 bg-gradient-to-r from-purple-100 to-cyan-100">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">💼 Venta al Mayoreo</h3>
            <p className="text-lg text-gray-600 mb-6">
              ¿Tienes una tienda o negocio? ¡Pregunta por nuestros precios especiales de mayoreo!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                className="bg-gradient-to-r from-purple-500 to-cyan-500 hover:from-purple-600 hover:to-cyan-600 text-white font-semibold"
                onClick={() => window.open('https://wa.me/525574918395?text=Hola!%20Me%20interesa%20información%20sobre%20venta%20al%20mayoreo%20de%20DI%20Candy%20Rush', '_blank')}
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                Preguntar por WhatsApp
              </Button>
              <Button 
                size="lg"
                variant="outline"
                className="border-purple-500 text-purple-600 hover:bg-purple-50"
                onClick={() => navigate('/wholesale')}
              >
                Formulario de Contacto
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_american-treats/artifacts/fa38658k_JUNTOS-2-PNG.png"
              alt="DI Candy Rush"
              className="h-20 mx-auto mb-4"
            />
            <p className="text-gray-400 mb-6">
              Trayéndote el auténtico sabor de los dulces americanos, importados directamente a México.
            </p>
            
            {/* Contact Links */}
            <div className="flex justify-center space-x-6 mb-6">
              <Button 
                variant="ghost" 
                className="text-gray-400 hover:text-white"
                onClick={() => window.open('https://wa.me/525574918395', '_blank')}
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                WhatsApp
              </Button>
              <Button 
                variant="ghost" 
                className="text-gray-400 hover:text-white"
                onClick={() => window.open('https://www.instagram.com/dicandyrush?igsh=MTZ3bHRsNThtODI1Zw==', '_blank')}
              >
                <Instagram className="w-5 h-5 mr-2" />
                Instagram
              </Button>
              <Button 
                variant="ghost" 
                className="text-gray-400 hover:text-white"
                onClick={() => window.open('mailto:contacto@dicandyrush.com.mx', '_blank')}
              >
                <Mail className="w-5 h-5 mr-2" />
                Contacto
              </Button>
            </div>

            {/* Legal Links */}
            <div className="flex justify-center space-x-8 mb-6 text-sm">
              <Button 
                variant="link" 
                className="text-gray-400 hover:text-cyan-300 p-0"
                onClick={() => navigate('/privacy-policy')}
              >
                📋 Aviso de Privacidad
              </Button>
              <Button 
                variant="link" 
                className="text-gray-400 hover:text-cyan-300 p-0"
                onClick={() => navigate('/terms-conditions')}
              >
                📄 Términos y Condiciones
              </Button>
            </div>

            {/* Business Info */}
            <div className="text-xs text-gray-500 mb-4 space-y-1">
              <p><strong>Irving Serafín Solís</strong> - DICANDYRUSH</p>
              <p>Tactoani #75, Col. Ricardo Flores Magón, C.P. 09820, Iztapalapa, CDMX</p>
              <p>Email: <a href="mailto:contacto@dicandyrush.com.mx" className="text-cyan-400 hover:text-cyan-300">contacto@dicandyrush.com.mx</a></p>
            </div>

            <p className="text-sm text-gray-500">
              © 2024 DI Candy Rush. Todos los derechos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

// Cookie Banner Component
const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Check if user has already accepted cookies
    const cookiesAccepted = localStorage.getItem('diCandyRush_cookies_accepted');
    if (!cookiesAccepted) {
      setShowBanner(true);
    }
  }, []);

  const acceptCookies = () => {
    localStorage.setItem('diCandyRush_cookies_accepted', 'true');
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-gray-900 text-white p-4 shadow-lg z-50 border-t-4 border-cyan-500">
      <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="text-sm">
          <p>
            🍪 <strong>Aceptación de Cookies:</strong> En DICANDYRUSH usamos cookies para mejorar tu experiencia de compra. Si continúas navegando, consideramos que aceptas su uso. Consulta más detalles en nuestro{' '}
            <Button 
              variant="link" 
              className="text-cyan-300 hover:text-cyan-100 p-0 h-auto text-sm underline"
              onClick={() => window.open('/privacy-policy', '_blank')}
            >
              Aviso de Privacidad
            </Button>.
          </p>
        </div>
        <div className="flex space-x-2 shrink-0">
          <Button 
            onClick={acceptCookies}
            size="sm"
            className="bg-cyan-500 hover:bg-cyan-600 text-white"
          >
            Aceptar Cookies
          </Button>
          <Button 
            onClick={() => setShowBanner(false)}
            variant="outline"
            size="sm"
            className="border-gray-600 text-gray-300 hover:bg-gray-800"
          >
            Cerrar
          </Button>
        </div>
      </div>
    </div>
  );
};

// Legal Pages Components
const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-pink-50 to-purple-50">
      <header className="bg-white shadow-lg border-b-4 border-gradient-candy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Button 
              onClick={() => window.history.back()}
              variant="ghost"
              className="text-cyan-600 hover:text-cyan-700"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver
            </Button>
            <img 
              src="https://customer-assets.emergentagent.com/job_american-treats/artifacts/fa38658k_JUNTOS-2-PNG.png"
              alt="DI Candy Rush"
              className="h-12 w-auto"
            />
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto p-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-3xl text-cyan-600">Aviso de Privacidad</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-lg max-w-none">
            <p>Irving Serafín Solís (en adelante identificado como <strong>"DICANDYRUSH"</strong>), con domicilio en Tactoani #75, Col. Ricardo Flores Magón, C.P. 09820, Iztapalapa, Ciudad de México, es el responsable del tratamiento de sus datos personales.</p>
            
            <h3 className="text-xl font-semibold text-pink-600 mt-6">¿Qué datos personales recabamos?</h3>
            <p>Para las finalidades señaladas en este aviso, recabamos los siguientes datos:</p>
            <ul>
              <li>Nombre completo</li>
              <li>Correo electrónico</li>
              <li>Dirección de envío y facturación</li>
              <li>Número telefónico</li>
            </ul>

            <h3 className="text-xl font-semibold text-pink-600 mt-6">Finalidades del tratamiento de datos:</h3>
            <p>Los datos personales que recabamos serán utilizados para las siguientes finalidades primarias, las cuales son necesarias para el servicio solicitado:</p>
            <ul>
              <li>Procesar y gestionar sus pedidos de productos</li>
              <li>Realizar el envío de sus productos a través de proveedores de mensajería</li>
              <li>Gestionar los pagos de sus compras</li>
              <li>Emitir facturas y comprobantes de compra</li>
              <li>Brindar atención al cliente y seguimiento de pedidos</li>
            </ul>

            <p>Asimismo, utilizaremos su información para las siguientes finalidades secundarias:</p>
            <ul>
              <li>Envío de publicidad, promociones y ofertas exclusivas de DICANDYRUSH</li>
              <li>Envío de boletines informativos</li>
            </ul>

            <p>Si usted no desea que sus datos sean tratados para estas finalidades secundarias, puede enviar un correo electrónico en cualquier momento a: <a href="mailto:contacto@dicandyrush.com.mx" className="text-cyan-600">contacto@dicandyrush.com.mx</a>.</p>

            <h3 className="text-xl font-semibold text-pink-600 mt-6">Transferencia de datos:</h3>
            <p>Le informamos que sus datos personales son compartidos con las siguientes empresas para el cumplimiento del servicio:</p>
            <ul>
              <li><strong>UPS:</strong> Para la logística y entrega de productos</li>
              <li><strong>BBVA y Mercado Pago:</strong> Para el procesamiento de los pagos electrónicos de forma segura</li>
            </ul>

            <h3 className="text-xl font-semibold text-pink-600 mt-6">Derechos ARCO (Acceso, Rectificación, Cancelación y Oposición):</h3>
            <p>Usted tiene derecho a conocer qué datos tenemos de usted, para qué los usamos y las condiciones del uso que les damos. Usted puede ejercer estos derechos enviando una solicitud al correo: <a href="mailto:contacto@dicandyrush.com.mx" className="text-cyan-600">contacto@dicandyrush.com.mx</a>. Su solicitud será atendida en un plazo máximo de 20 días hábiles.</p>

            <div className="bg-cyan-50 p-4 rounded-lg mt-6">
              <h4 className="font-semibold text-cyan-800">Aviso de Privacidad Simplificado:</h4>
              <p className="text-sm text-cyan-700">Irving Serafín Solís (DICANDYRUSH) utilizará sus datos personales (nombre, correo, dirección y teléfono) para gestionar su compra y envío. Sus datos serán compartidos con UPS y pasarelas de pago para completar el servicio. Para conocer el aviso completo y ejercer sus derechos ARCO, consulte este documento.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const TermsConditions = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-pink-50 to-purple-50">
      <header className="bg-white shadow-lg border-b-4 border-gradient-candy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Button 
              onClick={() => window.history.back()}
              variant="ghost"
              className="text-cyan-600 hover:text-cyan-700"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver
            </Button>
            <img 
              src="https://customer-assets.emergentagent.com/job_american-treats/artifacts/fa38658k_JUNTOS-2-PNG.png"
              alt="DI Candy Rush"
              className="h-12 w-auto"
            />
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto p-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-3xl text-cyan-600">Términos y Condiciones</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-lg max-w-none">
            <p>Bienvenido a <strong>DICANDYRUSH</strong>. Al utilizar este sitio web, usted acepta cumplir con los siguientes términos:</p>

            <h3 className="text-xl font-semibold text-pink-600 mt-6">1. Generalidades</h3>
            <p>Este sitio es operado por Irving Serafín Solís. Al realizar una compra, usted declara ser mayor de edad.</p>

            <h3 className="text-xl font-semibold text-pink-600 mt-6">2. Productos</h3>
            <p>DICANDYRUSH se especializa en la venta de productos comestibles. Las imágenes son ilustrativas. Debido a la naturaleza de los productos (comestibles), <strong>no se aceptan devoluciones una vez abierto el empaque original</strong> por razones de higiene y salud, salvo que el producto presente defectos de fabricación o caducidad vencida al momento de la entrega.</p>

            <h3 className="text-xl font-semibold text-pink-600 mt-6">3. Pagos</h3>
            <p>Los pagos se procesan a través de <strong>BBVA y Mercado Pago</strong>. DICANDYRUSH no almacena los números de sus tarjetas de crédito o débito.</p>

            <h3 className="text-xl font-semibold text-pink-600 mt-6">4. Envíos</h3>
            <p>Los envíos se realizan a través de <strong>UPS</strong>. Los tiempos de entrega son estimaciones y pueden variar según la zona de cobertura.</p>

            <h3 className="text-xl font-semibold text-pink-600 mt-6">5. Jurisdicción</h3>
            <p>Para cualquier controversia, las partes se someten a las leyes aplicables en la Ciudad de México, México.</p>

            <div className="bg-yellow-50 p-4 rounded-lg mt-6 border-l-4 border-yellow-400">
              <h4 className="font-semibold text-yellow-800">⚠️ Información Importante:</h4>
              <ul className="text-sm text-yellow-700 mt-2">
                <li>• <strong>SSL Seguro:</strong> Todas las transacciones están protegidas con certificado SSL</li>
                <li>• <strong>Facturación:</strong> Para solicitar factura (CFDI), envíe su constancia de situación fiscal a contacto@dicandyrush.com.mx</li>
                <li>• <strong>Cookies:</strong> Utilizamos cookies para mejorar su experiencia de compra</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Wholesale Contact Component
const WholesaleContact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/wholesale/inquiry`, formData);
      
      toast({
        title: "¡Consulta enviada!",
        description: "Te contactaremos pronto con información de mayoreo.",
      });
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        message: ''
      });
      
      // Redirect after 2 seconds
      setTimeout(() => {
        navigate('/');
      }, 2000);
      
    } catch (error) {
      toast({
        title: "Error",
        description: "Hubo un problema al enviar tu consulta. Inténtalo de nuevo.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-cyan-50 to-pink-50">
      <header className="bg-white shadow-lg border-b-4 border-gradient-candy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Button 
              onClick={() => navigate('/')}
              variant="ghost"
              className="text-cyan-600 hover:text-cyan-700"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver a la Tienda
            </Button>
            <img 
              src="https://customer-assets.emergentagent.com/job_american-treats/artifacts/fa38658k_JUNTOS-2-PNG.png"
              alt="DI Candy Rush"
              className="h-12 w-auto"
            />
          </div>
        </div>
      </header>

      <div className="max-w-2xl mx-auto p-6">
        <Card>
          <CardHeader className="text-center">
            <CardTitle className="text-3xl text-purple-600">💼 Consulta de Mayoreo</CardTitle>
            <CardDescription>
              Completa el formulario y te contactaremos con información especial de precios al mayoreo
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Nombre Completo *</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div>
                <Label htmlFor="phone">Teléfono *</Label>
                <Input
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div>
                <Label htmlFor="company">Nombre de la Empresa</Label>
                <Input
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                />
              </div>

              <div>
                <Label htmlFor="message">Mensaje *</Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Cuéntanos sobre tu negocio y qué productos te interesan..."
                  required
                />
              </div>

              <Button 
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-purple-500 to-cyan-500 hover:from-purple-600 hover:to-cyan-600"
              >
                {isSubmitting ? 'Enviando...' : 'Enviar Consulta'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Shopping Cart Component
const ShoppingCartPage = () => {
  const [cart, setCart] = useState([]);
  const [shippingInfo, setShippingInfo] = useState(null);
  const [products, setProducts] = useState([]);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    loadCart();
    cartFetchProducts();
  }, []);

  useEffect(() => {
    if (cart.length > 0) {
      calculateShipping();
    }
  }, [cart]);

  const loadCart = () => {
    const savedCart = localStorage.getItem('diCandyCart');
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    }
  };

  const cartFetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const calculateShipping = async () => {
    try {
      const response = await axios.post(`${API}/shipping/calculate`, cart);
      setShippingInfo(response.data);
    } catch (error) {
      console.error('Error calculating shipping:', error);
    }
  };

  const updateQuantity = (productId, newQuantity) => {
    if (newQuantity === 0) {
      removeItem(productId);
      return;
    }

    // Buscar el producto para verificar el stock disponible
    const product = products.find(p => p.id === productId);
    if (product && newQuantity > product.stock) {
      alert(`No puedes agregar más de ${product.stock} unidades de este producto (stock disponible).`);
      return;
    }

    const newCart = cart.map(item =>
      item.product_id === productId
        ? { ...item, quantity: newQuantity }
        : item
    );
    
    setCart(newCart);
    localStorage.setItem('diCandyCart', JSON.stringify(newCart));
  };

  const removeItem = (productId) => {
    const newCart = cart.filter(item => item.product_id !== productId);
    setCart(newCart);
    localStorage.setItem('diCandyCart', JSON.stringify(newCart));
    toast({
      title: "Producto eliminado",
      description: "El producto fue eliminado del carrito",
    });
  };

  const subtotal = cart.reduce((sum, item) => sum + (parseFloat(item.price) * item.quantity), 0);
  const totalWeight = cart.reduce((sum, item) => sum + (item.weight_kg * item.quantity), 0);
  const shipping = shippingInfo ? shippingInfo.shipping_cost : 0;
  const total = subtotal + shipping;
  
  // Wholesale threshold - only count products WITHOUT discount (they don't participate in wholesale)
  const WHOLESALE_THRESHOLD = 3000;
  const subtotalEligibleForWholesale = cart
    .filter(item => !item.has_discount)
    .reduce((sum, item) => sum + (parseFloat(item.price) * item.quantity), 0);
  const amountToWholesale = WHOLESALE_THRESHOLD - subtotalEligibleForWholesale;

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-pink-50 to-purple-50">
      {/* Header */}
      <header className="bg-white shadow-lg border-b-4 border-gradient-candy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Button 
              onClick={() => navigate('/')}
              variant="ghost"
              className="text-cyan-600 hover:text-cyan-700"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Seguir Comprando
            </Button>
            <img 
              src="https://customer-assets.emergentagent.com/job_american-treats/artifacts/fa38658k_JUNTOS-2-PNG.png"
              alt="DI Candy Rush"
              className="h-12 w-auto"
            />
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto p-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl flex items-center" data-testid="cart-title">
              <ShoppingCart className="w-6 h-6 mr-2" />
              Carrito de Compras
            </CardTitle>
          </CardHeader>
          <CardContent>
            {cart.length === 0 ? (
              <div className="text-center py-12">
                <ShoppingCart className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500 mb-4">Tu carrito está vacío</p>
                <Button onClick={() => navigate('/')}>
                  Seguir Comprando
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {cart.map((item) => (
                  <div key={item.product_id} className="flex items-center space-x-4 p-4 border rounded-lg">
                    <img 
                      src={getImageUrl(item.image_url)}
                      alt={item.name}
                      className="w-16 h-16 object-cover rounded"
                    />
                    <div className="flex-1">
                      <h3 className="font-semibold">{item.name}</h3>
                      <p className="text-pink-600 font-bold">${item.price} MXN</p>
                      {item.weight_display > 0 && (
                        <p className="text-sm text-gray-500 flex items-center">
                          <Scale className="w-3 h-3 mr-1" />
                          {item.weight_display}{item.weight_unit || 'g'} cada uno
                        </p>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                        data-testid={`decrease-quantity-${item.product_id}`}
                      >
                        <Minus className="w-3 h-3" />
                      </Button>
                      <span className="w-8 text-center" data-testid={`quantity-${item.product_id}`}>
                        {item.quantity}
                      </span>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                        data-testid={`increase-quantity-${item.product_id}`}
                      >
                        <Plus className="w-3 h-3" />
                      </Button>
                    </div>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => removeItem(item.product_id)}
                      data-testid={`remove-item-${item.product_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}

                <Separator />

                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Subtotal:</span>
                    <span data-testid="subtotal">${subtotal.toFixed(2)} MXN</span>
                  </div>
                  
                  {shippingInfo && (
                    <div className="flex justify-between">
                      <span className="flex items-center">
                        <Truck className="w-4 h-4 mr-1" />
                        Envío UPS ({totalWeight.toFixed(1)}kg):
                      </span>
                      <span data-testid="shipping">
                        ${shipping.toFixed(2)} MXN
                      </span>
                    </div>
                  )}
                  
                  <div className="flex justify-between text-lg font-bold">
                    <span>Total:</span>
                    <span data-testid="total">${total.toFixed(2)} MXN</span>
                  </div>
                  
                  {shippingInfo && (
                    <p className="text-sm text-gray-500 mt-2">
                      {shippingInfo.shipping_method}
                    </p>
                  )}
                </div>
                
                {/* Wholesale pricing notice - Solo visible si WHOLESALE_ENABLED es true */}
                {WHOLESALE_ENABLED && (() => {
                  const itemsWithDiscount = cart.filter(item => item.has_discount);
                  const itemsWithoutDiscount = cart.filter(item => !item.has_discount);
                  const hasOnlyDiscountedItems = itemsWithDiscount.length > 0 && itemsWithoutDiscount.length === 0;
                  const hasMixedItems = itemsWithDiscount.length > 0 && itemsWithoutDiscount.length > 0;
                  const hasOnlyRegularItems = itemsWithDiscount.length === 0;
                  
                  // If only discounted items, don't show wholesale notice
                  if (hasOnlyDiscountedItems) {
                    return (
                      <div className="p-4 bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-200 rounded-lg">
                        <div className="flex items-start space-x-3">
                          <span className="text-2xl">🏷️</span>
                          <div>
                            <p className="font-semibold text-orange-700">Productos con descuento de oferta</p>
                            <p className="text-sm text-orange-600 mt-1">
                              Todos los productos en tu carrito ya tienen descuento de oferta aplicado.
                            </p>
                            <p className="text-xs text-gray-500 mt-2">
                              Los productos con oferta no participan en el precio de mayoreo.
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  }
                  
                  // If mixed items (some with discount, some without)
                  if (hasMixedItems) {
                    if (amountToWholesale > 0) {
                      return (
                        <div className="p-4 bg-gradient-to-r from-cyan-50 to-pink-50 border border-cyan-200 rounded-lg">
                          <div className="flex items-start space-x-3">
                            <span className="text-2xl">💼</span>
                            <div>
                              <p className="font-semibold text-gray-800">¡Estás cerca del precio de mayoreo!</p>
                              <p className="text-sm text-gray-600 mt-1">
                                Agrega <span className="font-bold text-pink-600">${amountToWholesale.toFixed(2)} MXN</span> más en productos para obtener <span className="text-green-600 font-semibold">descuentos de mayoreo</span>
                              </p>
                              <div className="mt-3 p-2 bg-orange-50 border border-orange-200 rounded">
                                <p className="text-xs text-orange-700 font-semibold">⚠️ Productos que NO entran en mayoreo (ya tienen oferta):</p>
                                <ul className="text-xs text-orange-600 mt-1">
                                  {itemsWithDiscount.map(item => (
                                    <li key={item.product_id}>• {item.name} (-{item.discount_percentage}% oferta)</li>
                                  ))}
                                </ul>
                              </div>
                              <p className="text-xs text-gray-500 mt-2">
                                El precio de mayoreo aplica en compras ≥ $3,000 MXN (sin incluir envío)
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    } else {
                      return (
                        <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg">
                          <div className="flex items-start space-x-3">
                            <span className="text-2xl">🎉</span>
                            <div>
                              <p className="font-semibold text-green-700">¡Precio de mayoreo desbloqueado!</p>
                              <p className="text-sm text-green-600 mt-1">
                                Tu compra supera $3,000 MXN. Verás los descuentos aplicados en el checkout.
                              </p>
                              <div className="mt-3 p-2 bg-orange-50 border border-orange-200 rounded">
                                <p className="text-xs text-orange-700 font-semibold">⚠️ Productos que NO entran en mayoreo (ya tienen oferta):</p>
                                <ul className="text-xs text-orange-600 mt-1">
                                  {itemsWithDiscount.map(item => (
                                    <li key={item.product_id}>• {item.name} (-{item.discount_percentage}% oferta)</li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    }
                  }
                  
                  // If only regular items (no discounts)
                  if (hasOnlyRegularItems) {
                    if (amountToWholesale > 0) {
                      return (
                        <div className="p-4 bg-gradient-to-r from-cyan-50 to-pink-50 border border-cyan-200 rounded-lg">
                          <div className="flex items-start space-x-3">
                            <span className="text-2xl">💼</span>
                            <div>
                              <p className="font-semibold text-gray-800">¡Estás cerca del precio de mayoreo!</p>
                              <p className="text-sm text-gray-600 mt-1">
                                Agrega <span className="font-bold text-pink-600">${amountToWholesale.toFixed(2)} MXN</span> más en productos para obtener <span className="text-green-600 font-semibold">descuentos de mayoreo</span>
                              </p>
                              <p className="text-xs text-gray-500 mt-2">
                                El precio de mayoreo aplica en compras de productos ≥ $3,000 MXN (sin incluir envío)
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    } else {
                      return (
                        <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg">
                          <div className="flex items-start space-x-3">
                            <span className="text-2xl">🎉</span>
                            <div>
                              <p className="font-semibold text-green-700">¡Precio de mayoreo desbloqueado!</p>
                              <p className="text-sm text-green-600 mt-1">
                                Tu compra supera $3,000 MXN. Verás los descuentos aplicados en el checkout.
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    }
                  }
                  
                  return null;
                })()}

                <Button 
                  onClick={() => navigate('/checkout')}
                  className="w-full bg-gradient-to-r from-cyan-500 to-pink-500 hover:from-cyan-600 hover:to-pink-600 text-white font-semibold py-3"
                  data-testid="proceed-to-checkout-button"
                >
                  Proceder al Checkout
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Checkout Component  
const Checkout = () => {
  const [cart, setCart] = useState([]);
  const [shippingInfo, setShippingInfo] = useState(null);
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    // Campos de dirección separados para México
    street: '',
    exterior_number: '',
    interior_number: '',
    neighborhood: '',
    postal_code: '',
    municipality: '',
    city: '',
    country: 'México',
    payment_method: 'mercadopago_wallet'
  });
  const [preferenceId, setPreferenceId] = useState(null);
  const [showCardPayment, setShowCardPayment] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    loadCart();
  }, []);

  useEffect(() => {
    if (cart.length > 0) {
      calculateShipping();
    }
  }, [cart]);

  const loadCart = () => {
    const savedCart = localStorage.getItem('diCandyCart');
    if (savedCart) {
      const cartData = JSON.parse(savedCart);
      setCart(cartData);
      if (cartData.length === 0) {
        navigate('/');
      }
    } else {
      navigate('/');
    }
  };

  const calculateShipping = async () => {
    try {
      const response = await axios.post(`${API}/shipping/calculate`, cart);
      setShippingInfo(response.data);
    } catch (error) {
      console.error('Error calculating shipping:', error);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const createMercadoPagoPreference = async () => {
    try {
      setIsProcessing(true);
      
      // 1. PRIMERO crear el pedido en la base de datos
      const fullAddress = `${formData.street} ${formData.exterior_number}${formData.interior_number ? ', Int. ' + formData.interior_number : ''}, ${formData.neighborhood}, ${formData.postal_code} ${formData.municipality}, ${formData.city}, ${formData.country}`;
      
      const orderData = {
        items: cart.map(item => ({
          product_id: item.product_id,
          name: item.name,
          price: parseFloat(item.price),
          quantity: item.quantity,
          image_url: item.image_url,
          weight_kg: item.weight_kg || 0
        })),
        customer_name: formData.customer_name,
        customer_email: formData.customer_email,
        customer_phone: formData.customer_phone,
        shipping_address: fullAddress,
        shipping_cost: shippingInfo ? shippingInfo.shipping_cost : 0,
        total_weight: cart.reduce((sum, item) => sum + ((item.weight_kg || 0) * item.quantity), 0),
        payment_method: 'mercadopago_wallet',
        payment_status: 'pending'
      };

      const orderResponse = await axios.post(`${API}/orders`, orderData);
      const order = orderResponse.data;
      
      // Guardar el ID del pedido para usarlo después
      localStorage.setItem('pendingOrderId', order.id);

      // 2. DESPUÉS crear la preferencia de Mercado Pago con el ID del pedido
      const items = cart.map(item => {
        const currentPrice = parseFloat(item.price);
        const wholesalePrice = item.wholesale_price ? parseFloat(item.wholesale_price) : currentPrice;
        
        let priceToUse;
        if (item.has_discount) {
          priceToUse = currentPrice;
        } else if (WHOLESALE_ENABLED && isWholesale && item.wholesale_price) {
          priceToUse = wholesalePrice;
        } else {
          priceToUse = currentPrice;
        }
        
        return {
          title: item.name,
          quantity: item.quantity,
          unit_price: priceToUse,
          currency_id: 'MXN'
        };
      });

      // Add shipping cost as a separate item if available
      if (shippingInfo && shippingInfo.shipping_cost > 0) {
        items.push({
          title: `Envío ${shippingInfo.shipping_method || 'UPS'}`,
          quantity: 1,
          unit_price: shippingInfo.shipping_cost,
          currency_id: 'MXN'
        });
      }

      const preferenceData = {
        items: items,
        back_urls: {
          success: `${window.location.origin}/order-confirmation/${order.id}`,
          failure: `${window.location.origin}/checkout`,
          pending: `${window.location.origin}/order-confirmation/${order.id}`
        },
        auto_return: 'approved',
        external_reference: order.id,
        payer: {
          email: formData.customer_email
        }
      };

      const response = await axios.post(`${API}/mercadopago/create-preference`, preferenceData);
      
      if (response.data.success) {
        setPreferenceId(response.data.id);
        // Limpiar carrito después de crear el pedido
        localStorage.removeItem('diCandyCart');
        setCart([]);
        setIsProcessing(false);
        return response.data.id;
      } else {
        throw new Error('Failed to create payment preference');
      }
    } catch (error) {
      console.error('Error creating Mercado Pago preference:', error);
      setIsProcessing(false);
      toast({
        title: "Error",
        description: "Error al crear el pedido. Por favor intenta de nuevo.",
        variant: "destructive"
      });
      return null;
    }
  };

  const handleCardPayment = async (formData) => {
    setIsProcessing(true);
    
    try {
      // First create the order
      const fullAddress = `${formData.street} ${formData.exterior_number}${formData.interior_number ? ', Int. ' + formData.interior_number : ''}, ${formData.neighborhood}, ${formData.postal_code} ${formData.municipality}, ${formData.city}, ${formData.country}`;
      
      const orderData = {
        items: cart,
        customer_name: formData.customer_name,
        customer_email: formData.customer_email,
        customer_phone: formData.customer_phone,
        shipping_address: fullAddress,
        payment_method: 'mercadopago_card'
      };

      const orderResponse = await axios.post(`${API}/orders`, orderData);
      const order = orderResponse.data;

      // Process payment with Mercado Pago
      const paymentData = {
        transaction_amount: subtotal + shipping,
        token: formData.token,
        description: `Pedido #${order.id} - DI Candy Rush`,
        payment_method_id: formData.payment_method_id,
        installments: formData.installments,
        payer_email: formData.customer_email,
        external_reference: order.id
      };

      const paymentResponse = await axios.post(`${API}/mercadopago/process-payment`, paymentData);

      if (paymentResponse.data.success) {
        const payment = paymentResponse.data.payment;
        
        if (payment.status === 'approved') {
          localStorage.removeItem('diCandyCart');
          
          toast({
            title: "¡Pago aprobado!",
            description: "Tu pedido ha sido procesado exitosamente.",
          });
          
          navigate(`/order-confirmation/${order.id}`);
        } else if (payment.status === 'pending') {
          localStorage.removeItem('diCandyCart');
          
          toast({
            title: "Pago pendiente",
            description: "Tu pago está siendo procesado. Te notificaremos cuando esté aprobado.",
          });
          
          navigate(`/order-confirmation/${order.id}`);
        } else {
          toast({
            title: "Pago rechazado",
            description: "Tu pago fue rechazado. Por favor, intenta con otra tarjeta.",
            variant: "destructive"
          });
        }
      } else {
        throw new Error('Payment processing failed');
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast({
        title: "Error en el pago",
        description: error.response?.data?.detail || "Error al procesar el pago",
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleTestPayment = async () => {
    setIsProcessing(true);
    
    try {
      // First create the order
      const fullAddress = `${formData.street} ${formData.exterior_number}${formData.interior_number ? ', Int. ' + formData.interior_number : ''}, ${formData.neighborhood}, ${formData.postal_code} ${formData.municipality}, ${formData.city}, ${formData.country}`;
      
      const orderData = {
        items: cart,
        customer_name: formData.customer_name,
        customer_email: formData.customer_email,
        customer_phone: formData.customer_phone,
        shipping_address: fullAddress,
        payment_method: 'test_payment'
      };

      const orderResponse = await axios.post(`${API}/orders`, orderData);
      const order = orderResponse.data;

      // Process test payment
      const paymentData = {
        order_id: order.id,
        amount: total,
        currency: 'MXN',
        customer_email: formData.customer_email
      };

      const paymentResponse = await axios.post(`${API}/payments/test-payment`, paymentData);

      if (paymentResponse.data.status === 'completed') {
        localStorage.removeItem('diCandyCart');
        
        toast({
          title: "¡Pago de prueba exitoso! 🧪",
          description: "El sistema funcionó correctamente. Revisa tu email para la confirmación.",
        });
        
        navigate(`/order-confirmation/${order.id}`);
      } else {
        toast({
          title: "Pago de prueba fallido",
          description: "Se simuló un fallo de pago. Intenta de nuevo para probar diferentes escenarios.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Test payment error:', error);
      toast({
        title: "Error en el pago de prueba",
        description: error.response?.data?.detail || "Error al procesar el pago de prueba",
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form data
    if (!formData.customer_name || !formData.customer_email || !formData.customer_phone) {
      toast({
        title: "Campos requeridos",
        description: "Por favor completa todos los campos obligatorios",
        variant: "destructive"
      });
      return;
    }

    // Basic email validation (RFC-ish) + common typo detection
    const email = formData.customer_email.trim().toLowerCase();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    if (!emailRegex.test(email)) {
      toast({
        title: "Email inválido",
        description: "Por favor revisa que tu correo tenga un formato válido (ej: nombre@dominio.com)",
        variant: "destructive"
      });
      return;
    }

    // Detect common typos in the domain: .con, .cmo, .comm, gmial, hotmial, hotnail, yahho, etc.
    const commonTypos = [
      { bad: /\.con$/, good: '.com' },
      { bad: /\.cmo$/, good: '.com' },
      { bad: /\.comm$/, good: '.com' },
      { bad: /\.ocm$/, good: '.com' },
      { bad: /\.mx\.mx$/, good: '.mx' },
      { bad: /@gmial\./, good: '@gmail.' },
      { bad: /@gmai\./, good: '@gmail.' },
      { bad: /@hotmial\./, good: '@hotmail.' },
      { bad: /@hotnail\./, good: '@hotmail.' },
      { bad: /@yahho\./, good: '@yahoo.' },
      { bad: /@yaho\./, good: '@yahoo.' },
      { bad: /@outlok\./, good: '@outlook.' },
    ];
    for (const t of commonTypos) {
      if (t.bad.test(email)) {
        toast({
          title: "Revisa tu correo",
          description: `Parece que hay un error de tipeo. ¿Quisiste decir "${t.good}"? Corrige tu correo para que puedas recibir la confirmación.`,
          variant: "destructive"
        });
        return;
      }
    }

    if (formData.payment_method === 'test_payment') {
      // Process test payment
      await handleTestPayment();
    } else if (formData.payment_method === 'mercadopago_wallet') {
      // Create Mercado Pago preference for wallet payments
      const preferenceId = await createMercadoPagoPreference();
      if (preferenceId) {
        setPreferenceId(preferenceId);
      }
    } else if (formData.payment_method === 'mercadopago_card') {
      // Show card payment form
      setShowCardPayment(true);
    } else {
      // Invalid payment method - should not happen with the new interface
      toast({
        title: "Error de configuración",
        description: "Por favor selecciona un método de pago válido.",
        variant: "destructive"
      });
    }
  };

  // Calculate totals
  const regularSubtotal = cart.reduce((sum, item) => sum + (parseFloat(item.price) * item.quantity), 0);
  const shipping = shippingInfo ? shippingInfo.shipping_cost : 0;
  
  // Wholesale pricing applies when SUBTOTAL of eligible products >= $3,000 MXN
  // Products with active discount (oferta) are excluded from wholesale pricing calculation
  const WHOLESALE_THRESHOLD = 3000;
  
  // Calculate subtotal of products eligible for wholesale (those WITHOUT active discount)
  const subtotalEligibleForWholesale = cart
    .filter(item => !item.has_discount)
    .reduce((sum, item) => sum + (parseFloat(item.price) * item.quantity), 0);
  
  // Wholesale applies only when WHOLESALE_ENABLED and eligible products reach the threshold
  const isWholesale = WHOLESALE_ENABLED && subtotalEligibleForWholesale >= WHOLESALE_THRESHOLD;
  
  // Calculate wholesale subtotal if applicable
  // Products with has_discount (oferta activa) keep their discounted price and don't get wholesale price
  const wholesaleSubtotal = isWholesale ? cart.reduce((sum, item) => {
    // If item has active discount (oferta), use the already discounted price, not wholesale
    if (item.has_discount) {
      return sum + (parseFloat(item.price) * item.quantity);
    }
    // Otherwise, apply wholesale price if available
    const price = item.wholesale_price ? parseFloat(item.wholesale_price) : parseFloat(item.price);
    return sum + (price * item.quantity);
  }, 0) : regularSubtotal;
  
  const subtotal = isWholesale ? wholesaleSubtotal : regularSubtotal;
  const totalSavings = isWholesale ? (regularSubtotal - wholesaleSubtotal) : 0;
  
  const total = subtotal + shipping;
  const totalWeight = cart.reduce((sum, item) => sum + (item.weight_kg * item.quantity), 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-pink-50 to-purple-50">
      {/* Header */}
      <header className="bg-white shadow-lg border-b-4 border-gradient-candy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Button 
              onClick={() => navigate('/cart')}
              variant="ghost"
              className="text-cyan-600 hover:text-cyan-700"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver al Carrito
            </Button>
            <img 
              src="https://customer-assets.emergentagent.com/job_american-treats/artifacts/fa38658k_JUNTOS-2-PNG.png"
              alt="DI Candy Rush"
              className="h-12 w-auto"
            />
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto p-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Order Form */}
          <Card>
            <CardHeader>
              <CardTitle>Proceder al Checkout</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="customer_name">Nombre Completo *</Label>
                  <Input
                    id="customer_name"
                    name="customer_name"
                    value={formData.customer_name}
                    onChange={handleInputChange}
                    required
                    data-testid="customer-name-input"
                  />
                </div>

                <div>
                  <Label htmlFor="customer_email">Email *</Label>
                  <Input
                    id="customer_email"
                    name="customer_email"
                    type="email"
                    value={formData.customer_email}
                    onChange={handleInputChange}
                    required
                    data-testid="customer-email-input"
                  />
                  <p className="text-sm text-gray-500 mt-1 flex items-center">
                    <Mail className="w-3 h-3 mr-1" />
                    Recibirás confirmación por email
                  </p>
                </div>

                <div>
                  <Label htmlFor="customer_phone">Teléfono *</Label>
                  <Input
                    id="customer_phone"
                    name="customer_phone"
                    value={formData.customer_phone}
                    onChange={handleInputChange}
                    required
                    data-testid="customer-phone-input"
                  />
                </div>

                <Separator />
                <h3 className="text-lg font-semibold text-gray-900 mb-4">📍 Dirección de Envío</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="street">Calle *</Label>
                    <Input
                      id="street"
                      name="street"
                      value={formData.street}
                      onChange={handleInputChange}
                      required
                      placeholder="Ej: Av. Insurgentes Sur"
                      data-testid="street-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="exterior_number">Número Exterior *</Label>
                    <Input
                      id="exterior_number"
                      name="exterior_number"
                      value={formData.exterior_number}
                      onChange={handleInputChange}
                      required
                      placeholder="Ej: 123"
                      data-testid="exterior-number-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="interior_number">Número Interior</Label>
                    <Input
                      id="interior_number"
                      name="interior_number"
                      value={formData.interior_number}
                      onChange={handleInputChange}
                      placeholder="Ej: 4A, Depto 5 (opcional)"
                      data-testid="interior-number-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="neighborhood">Colonia *</Label>
                    <Input
                      id="neighborhood"
                      name="neighborhood"
                      value={formData.neighborhood}
                      onChange={handleInputChange}
                      required
                      placeholder="Ej: Roma Norte"
                      data-testid="neighborhood-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="postal_code">Código Postal *</Label>
                    <Input
                      id="postal_code"
                      name="postal_code"
                      value={formData.postal_code}
                      onChange={handleInputChange}
                      required
                      placeholder="Ej: 06700"
                      pattern="[0-9]{5}"
                      maxLength="5"
                      data-testid="postal-code-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="municipality">Delegación/Municipio *</Label>
                    <Input
                      id="municipality"
                      name="municipality"
                      value={formData.municipality}
                      onChange={handleInputChange}
                      required
                      placeholder="Ej: Cuauhtémoc"
                      data-testid="municipality-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="city">Ciudad *</Label>
                    <Input
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      required
                      placeholder="Ej: Ciudad de México"
                      data-testid="city-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="country">País</Label>
                  <Input
                    id="country"
                    name="country"
                    value={formData.country}
                    readOnly
                    className="bg-gray-100"
                    data-testid="country-input"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    🇲🇽 Actualmente solo realizamos envíos dentro de México
                  </p>
                </div>

                <div>
                  <Label>Método de Pago</Label>
                  <div className="mt-3 space-y-3">
                    {/* Mercado Pago Option */}
                    <div className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      formData.payment_method === 'mercadopago_wallet' 
                        ? 'bg-cyan-50 border-cyan-400' 
                        : 'bg-gray-50 border-gray-200 hover:border-cyan-300'
                    }`} onClick={() => setFormData({...formData, payment_method: 'mercadopago_wallet'})}>
                      <div className="flex items-center space-x-3">
                        <input
                          type="radio"
                          name="payment_method"
                          value="mercadopago_wallet"
                          checked={formData.payment_method === 'mercadopago_wallet'}
                          onChange={handleInputChange}
                          className="text-cyan-500"
                        />
                        <div>
                          <h3 className="font-semibold text-gray-900">💰 Mercado Pago</h3>
                          <p className="text-sm text-gray-600 mt-1">
                            Acepta <strong>todas las formas de pago</strong>: tarjetas de crédito/débito, pagos en efectivo (OXXO, Seven Eleven), transferencias bancarias y saldo de Mercado Pago.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <p className="text-sm text-gray-500">* Campos requeridos</p>

                {/* Privacy Notice */}
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <p className="text-xs text-blue-800">
                    <strong>Aviso de Privacidad Simplificado:</strong> Irving Serafín Solís (DICANDYRUSH) utilizará sus datos personales (nombre, correo, dirección y teléfono) para gestionar su compra y envío. Sus datos serán compartidos con UPS y pasarelas de pago para completar el servicio. Para conocer el aviso completo y ejercer sus derechos ARCO, visite: {' '}
                    <Button 
                      variant="link" 
                      className="text-blue-600 hover:text-blue-800 p-0 h-auto text-xs underline"
                      onClick={() => window.open('/privacy-policy', '_blank')}
                    >
                      Aviso de Privacidad Completo
                    </Button>
                  </p>
                </div>

                <Button 
                  type="submit"
                  disabled={isProcessing}
                  className="w-full bg-gradient-to-r from-cyan-500 to-pink-500 hover:from-cyan-600 hover:to-pink-600 text-white font-semibold py-3"
                  data-testid="place-order-button"
                >
                  {isProcessing ? 'Procesando...' : 'Realizar Pedido'}
                </Button>
              </form>

              {/* Mercado Pago Wallet */}
              {preferenceId && (
                <div className="mt-6 border-t pt-6">
                  <h3 className="text-lg font-semibold mb-4">Completar Pago con Mercado Pago</h3>
                  <Wallet initialization={{ preferenceId: preferenceId }} />
                </div>
              )}

              {/* Mercado Pago Card Payment */}
              {showCardPayment && (
                <div className="mt-6 border-t pt-6">
                  <h3 className="text-lg font-semibold mb-4">Pagar con Tarjeta</h3>
                  <CardPayment
                    initialization={{
                      amount: subtotal + shipping,
                    }}
                    onSubmit={async (param) => {
                      // Pass card form data to our payment handler
                      const cardData = {
                        ...formData,
                        token: param.token,
                        payment_method_id: param.payment_method_id,
                        installments: param.installments
                      };
                      await handleCardPayment(cardData);
                    }}
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Order Summary */}
          <Card>
            <CardHeader>
              <CardTitle>Resumen del Pedido</CardTitle>
              {isWholesale && (
                <div className="mt-2 p-3 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg">
                  <p className="text-green-700 font-semibold flex items-center">
                    💼 ¡Precio de Mayoreo Aplicado!
                  </p>
                  <p className="text-sm text-green-600">
                    Tu compra supera ${WHOLESALE_THRESHOLD.toLocaleString()} MXN
                  </p>
                </div>
              )}
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {cart.map((item) => {
                  const originalPrice = parseFloat(item.original_price || item.price);
                  const currentPrice = parseFloat(item.price);
                  const wholesalePrice = item.wholesale_price ? parseFloat(item.wholesale_price) : currentPrice;
                  
                  // Check if item has active discount (oferta) - these don't get wholesale
                  const hasActiveDiscount = item.has_discount;
                  // Wholesale only applies if no active discount AND wholesale is available AND we're in wholesale mode
                  const hasWholesalePrice = !hasActiveDiscount && item.wholesale_price && isWholesale;
                  
                  // Calculate final price: discount items keep discount price, wholesale items get wholesale price
                  const finalUnitPrice = hasActiveDiscount ? currentPrice : (hasWholesalePrice ? wholesalePrice : currentPrice);
                  const itemTotal = finalUnitPrice * item.quantity;
                  const itemSavings = hasWholesalePrice ? ((currentPrice - wholesalePrice) * item.quantity) : 0;
                  
                  // Calculate discount savings (for items with active offer)
                  const discountSavings = hasActiveDiscount ? ((originalPrice - currentPrice) * item.quantity) : 0;
                  
                  return (
                    <div key={item.product_id} className="border-b pb-3">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <span className="font-medium">{item.name} x {item.quantity}</span>
                          {/* Show discount badge if item has active offer */}
                          {hasActiveDiscount && (
                            <span className="ml-2 text-xs bg-orange-100 text-orange-600 px-2 py-0.5 rounded-full">
                              🏷️ -{item.discount_percentage}% Oferta
                            </span>
                          )}
                          {item.weight_display > 0 && (
                            <div className="text-sm text-gray-500 flex items-center">
                              <Scale className="w-3 h-3 mr-1" />
                              {(item.weight_display * item.quantity).toFixed(2)}{item.weight_unit || 'g'} total
                            </div>
                          )}
                        </div>
                        <div className="text-right">
                          {hasActiveDiscount ? (
                            <>
                              <span className="line-through text-gray-400 text-sm">
                                ${(originalPrice * item.quantity).toFixed(2)}
                              </span>
                              <span className="ml-2 text-orange-600 font-semibold">
                                ${itemTotal.toFixed(2)} MXN
                              </span>
                              <div className="text-xs text-orange-500">
                                Ahorras ${discountSavings.toFixed(2)} (Oferta)
                              </div>
                            </>
                          ) : hasWholesalePrice ? (
                            <>
                              <span className="line-through text-gray-400 text-sm">
                                ${(currentPrice * item.quantity).toFixed(2)}
                              </span>
                              <span className="ml-2 text-green-600 font-semibold">
                                ${itemTotal.toFixed(2)} MXN
                              </span>
                              <div className="text-xs text-green-500">
                                Ahorras ${itemSavings.toFixed(2)} (Mayoreo)
                              </div>
                            </>
                          ) : (
                            <span>${itemTotal.toFixed(2)} MXN</span>
                          )}
                        </div>
                      </div>
                      {/* Show per-unit price breakdown for wholesale - Solo si WHOLESALE_ENABLED */}
                      {WHOLESALE_ENABLED && hasWholesalePrice && !hasActiveDiscount && (
                        <div className="mt-1 text-xs text-gray-500 bg-green-50 p-2 rounded">
                          <span>💼 Precio mayoreo: </span>
                          <span className="line-through">${currentPrice.toFixed(2)}</span>
                          <span className="text-green-600 ml-1">→ ${wholesalePrice.toFixed(2)} MXN</span>
                        </div>
                      )}
                      {/* Show notice for items with discount that can't get wholesale - Solo si WHOLESALE_ENABLED */}
                      {WHOLESALE_ENABLED && hasActiveDiscount && isWholesale && item.wholesale_price && (
                        <div className="mt-1 text-xs text-orange-500 bg-orange-50 p-2 rounded">
                          🏷️ Este producto ya tiene descuento de oferta (no aplica mayoreo)
                        </div>
                      )}
                    </div>
                  );
                })}
                
                <Separator />
                
                {/* Ahorro mayoreo - Solo si WHOLESALE_ENABLED */}
                {WHOLESALE_ENABLED && isWholesale && totalSavings > 0 && (
                  <div className="flex justify-between text-green-600 bg-green-50 p-2 rounded">
                    <span className="font-medium">💰 Ahorro Total Mayoreo:</span>
                    <span className="font-bold">-${totalSavings.toFixed(2)} MXN</span>
                  </div>
                )}
                
                <div className="flex justify-between">
                  <span>Subtotal:</span>
                  <div className="text-right">
                    {isWholesale && totalSavings > 0 && (
                      <span className="line-through text-gray-400 text-sm mr-2">
                        ${regularSubtotal.toFixed(2)}
                      </span>
                    )}
                    <span>${subtotal.toFixed(2)} MXN</span>
                  </div>
                </div>
                
                <div className="flex justify-between">
                  <span className="flex items-center">
                    <Truck className="w-4 h-4 mr-1" />
                    Envío UPS ({totalWeight.toFixed(1)}kg):
                  </span>
                  <span>${shipping.toFixed(2)} MXN</span>
                </div>
                
                <div className="flex justify-between text-lg font-bold">
                  <span>Total:</span>
                  <span data-testid="checkout-total">${total.toFixed(2)} MXN</span>
                </div>
                
                {shippingInfo && (
                  <p className="text-sm text-gray-500">
                    {shippingInfo.shipping_method}
                  </p>
                )}
                
                {/* Tip de mayoreo - Solo visible si WHOLESALE_ENABLED es true */}
                {WHOLESALE_ENABLED && !isWholesale && regularSubtotal < WHOLESALE_THRESHOLD && (
                  <div className="mt-4 p-3 bg-gradient-to-r from-cyan-50 to-pink-50 border border-cyan-200 rounded-lg">
                    <p className="text-sm text-gray-700">
                      💡 <strong>¡Tip!</strong> Agrega ${(WHOLESALE_THRESHOLD - regularSubtotal).toFixed(2)} MXN más en productos para obtener <span className="text-green-600 font-semibold">precios de mayoreo</span>
                    </p>
                    <p className="text-xs text-gray-500 mt-1">El precio de mayoreo aplica en compras de productos ≥ $3,000 MXN (sin incluir envío)</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

// Order Confirmation Component
const OrderConfirmation = () => {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [searchParams] = useState(new URLSearchParams(window.location.search));

  useEffect(() => {
    // Verificar si viene de Mercado Pago con parámetros de pago
    const paymentId = searchParams.get('payment_id');
    const status = searchParams.get('status');
    const externalReference = searchParams.get('external_reference');
    
    if (paymentId && status) {
      // Actualizar el estado del pago en el backend
      updatePaymentStatus(paymentId, status, externalReference || orderId);
    }
    
    fetchOrder();
  }, [orderId]);

  const updatePaymentStatus = async (paymentId, status, orderRef) => {
    try {
      await axios.post(`${API}/orders/update-payment-status`, {
        order_id: orderRef,
        payment_id: paymentId,
        status: status
      });
      // Recargar el pedido para mostrar el estado actualizado
      setTimeout(() => fetchOrder(), 1000);
    } catch (error) {
      console.error('Error updating payment status:', error);
    }
  };

  const fetchOrder = async () => {
    try {
      const response = await axios.get(`${API}/orders/${orderId}`);
      setOrder(response.data);
    } catch (error) {
      console.error('Error fetching order:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-cyan-50 via-pink-50 to-purple-50">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-cyan-50 via-pink-50 to-purple-50">
        Orden no encontrada
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-pink-50 to-purple-50">
      <div className="max-w-2xl mx-auto p-6">
        <Card>
          <CardHeader className="text-center">
            <div className="mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_american-treats/artifacts/fa38658k_JUNTOS-2-PNG.png"
                alt="DI Candy Rush"
                className="h-16 mx-auto"
              />
            </div>
            <CardTitle className="text-3xl text-green-600" data-testid="order-confirmation-title">
              {order.payment_status === 'approved' ? '✅ ¡Pedido Confirmado!' : '⏳ Pedido Pendiente'}
            </CardTitle>
            <CardDescription>
              ID del Pedido: #{order.id.slice(-8)}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <h3 className="font-semibold mb-2">Detalles del Pedido:</h3>
              <div className="space-y-2">
                {order.items.map((item) => (
                  <div key={item.product_id} className="flex justify-between">
                    <span>{item.name} x {item.quantity}</span>
                    <span>${(parseFloat(item.price) * item.quantity).toFixed(2)} MXN</span>
                  </div>
                ))}
                <Separator />
                <div className="flex justify-between">
                  <span>Subtotal:</span>
                  <span>${parseFloat(order.total_amount).toFixed(2)} MXN</span>
                </div>
                <div className="flex justify-between">
                  <span className="flex items-center">
                    <Truck className="w-4 h-4 mr-1" />
                    Envío UPS ({order.total_weight}kg):
                  </span>
                  <span>${parseFloat(order.shipping_cost).toFixed(2)} MXN</span>
                </div>
                <div className="flex justify-between font-bold text-lg">
                  <span>Total:</span>
                  <span>${parseFloat(order.grand_total).toFixed(2)} MXN</span>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold mb-2">Información del Cliente:</h3>
              <p><strong>Nombre:</strong> {order.customer_name}</p>
              <p><strong>Email:</strong> {order.customer_email}</p>
              <p><strong>Teléfono:</strong> {order.customer_phone}</p>
            </div>

            <div>
              <h3 className="font-semibold mb-2">Estado del Pago:</h3>
              <Badge 
                variant={order.payment_status === 'approved' ? 'default' : 'secondary'}
                className={order.payment_status === 'approved' ? 'bg-green-500' : 'bg-yellow-500'}
              >
                {order.payment_status === 'approved' ? 'Aprobado' : 'Pendiente'}
              </Badge>
            </div>

            {order.payment_method === 'test_oxxo' && order.payment_status === 'pending' && (
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-blue-800">Instrucciones de Pago OXXO:</h4>
                <ol className="list-decimal list-inside text-sm text-blue-700 mt-2 space-y-1">
                  <li>Ve a cualquier tienda OXXO</li>
                  <li>Di al cajero que quieres hacer un pago de OXXO Pay</li>
                  <li>Proporciona esta referencia: <strong>{orderId.slice(-8)}</strong></li>
                  <li>Paga la cantidad exacta: ${parseFloat(order.grand_total).toFixed(2)} MXN</li>
                  <li>Conserva tu comprobante</li>
                </ol>
              </div>
            )}

            {order.ups_tracking_number && (
              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-semibold text-green-800 flex items-center">
                  <Truck className="w-4 h-4 mr-2" />
                  ¡Tu pedido ha sido enviado!
                </h4>
                <p className="text-green-700 mt-2">
                  <strong>Número de Guía UPS:</strong> {order.ups_tracking_number}
                </p>
                <p className="text-sm text-green-600 mt-1">
                  Rastrea tu paquete en el sitio web de UPS México
                </p>
              </div>
            )}

            <div className="bg-cyan-50 p-4 rounded-lg">
              <h4 className="font-semibold text-cyan-800 flex items-center">
                <Mail className="w-4 h-4 mr-2" />
                ¡Confirmación por Email Enviada!
              </h4>
              <p className="text-cyan-700 text-sm mt-1">
                Se ha enviado un email de confirmación a {order.customer_email}
              </p>
            </div>

            <Button 
              onClick={() => navigate('/')}
              className="w-full bg-gradient-to-r from-cyan-500 to-pink-500 hover:from-cyan-600 hover:to-pink-600"
              data-testid="continue-shopping-button"
            >
              Seguir Comprando
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Admin Panel Component
const AdminPanel = () => {
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeTab, setActiveTab] = useState('products');
  const [isAddingProduct, setIsAddingProduct] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [trackingModal, setTrackingModal] = useState({ open: false, orderId: '', orderEmail: '' });
  const [orderFilter, setOrderFilter] = useState('all'); // 'all', 'pending', 'paid', 'shipped'
  
  // Barcode functionality states
  const [barcodeSearch, setBarcodeSearch] = useState('');
  const [barcodeSearchResult, setBarcodeSearchResult] = useState(null);
  const [isSearchingBarcode, setIsSearchingBarcode] = useState(false);
  const [showBarcodeStockModal, setShowBarcodeStockModal] = useState(false);
  const [additionalStock, setAdditionalStock] = useState('');
  
  const [newProduct, setNewProduct] = useState({
    name: '',
    description: '',
    price: '',
    wholesale_price: '',  // Precio de mayoreo
    discount_percentage: 0,  // Descuento en porcentaje (0, 5, 10, 15, 20, 25)
    discount_ends_at: '',  // Fecha/hora cuando termina la oferta
    stock: '',
    weight_kg: '',
    weight_display: '',
    dimensions: '',
    category: 'Dulces',
    barcode: '',
    ingredients: '',  // Ingredientes del producto
    images: [],  // Array for multiple images
    existingImages: []  // URLs of existing images when editing
  });
  const { toast } = useToast();

  useEffect(() => {
    adminFetchProducts();
    fetchOrders();
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data.categories);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const adminFetchProducts = async () => {
    try {
      console.log('🔄 Fetching admin products...');
      const response = await axios.get(`${API}/admin/products`);
      console.log('📦 Received products:', response.data.length);
      console.log('📋 Product names:', response.data.map(p => p.name));
      
      // Force a complete state refresh
      setProducts([]);
      setTimeout(() => {
        setProducts(response.data);
        console.log('✅ Products updated in state');
      }, 100);
    } catch (error) {
      console.error('❌ Error fetching products:', error);
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/admin/orders`);
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };

  const resetForm = () => {
    setNewProduct({
      name: '',
      description: '',
      price: '',
      wholesale_price: '',
      discount_percentage: 0,
      discount_ends_at: '',
      stock: '',
      weight_kg: '',
      weight_display: '',
      dimensions: '',
      category: 'Dulces',
      barcode: '',
      ingredients: '',
      images: [],
      existingImages: []
    });
    setEditingProduct(null);
  };

  const handleEditProduct = async (productId) => {
    try {
      const response = await axios.get(`${API}/admin/products/${productId}`);
      const product = response.data;
      // Get existing images - use image_urls if available, otherwise use image_url
      const existingImages = product.image_urls && product.image_urls.length > 0 
        ? product.image_urls 
        : (product.image_url ? [product.image_url] : []);
      
      setNewProduct({
        name: product.name,
        description: product.description,
        price: product.price.toString(),
        wholesale_price: product.wholesale_price ? product.wholesale_price.toString() : '',
        discount_percentage: product.discount_percentage || 0,
        discount_ends_at: product.discount_ends_at ? product.discount_ends_at.slice(0, 16) : '',
        stock: product.stock.toString(),
        weight_kg: product.weight_kg.toString(),
        weight_display: product.weight_display ? product.weight_display.toString() : '',
        dimensions: product.dimensions || '',
        category: product.category,
        barcode: product.barcode || '',
        ingredients: product.ingredients || '',
        images: [],
        existingImages: existingImages
      });
      setEditingProduct(product);
      setIsAddingProduct(true);
    } catch (error) {
      toast({
        title: "Error",
        description: "Error al cargar el producto para editar.",
        variant: "destructive"
      });
    }
  };

  // Barcode search functionality
  const searchByBarcode = async () => {
    if (!barcodeSearch.trim()) {
      toast({
        title: "Error",
        description: "Por favor ingresa un código de barras.",
        variant: "destructive"
      });
      return;
    }

    setIsSearchingBarcode(true);
    try {
      const response = await axios.get(`${API}/admin/products/barcode/${barcodeSearch}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` }
      });

      if (response.data.found) {
        setBarcodeSearchResult(response.data);
        toast({
          title: "Producto encontrado",
          description: response.data.message,
        });
      } else {
        setBarcodeSearchResult({ found: false, message: response.data.message });
        toast({
          title: "Producto no encontrado",
          description: response.data.message,
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Error al buscar el código de barras.",
        variant: "destructive"
      });
      setBarcodeSearchResult(null);
    }
    setIsSearchingBarcode(false);
  };

  const addStockByBarcode = async () => {
    if (!additionalStock || parseInt(additionalStock) < 1) {
      toast({
        title: "Error",
        description: "Por favor ingresa una cantidad válida de stock.",
        variant: "destructive"
      });
      return;
    }

    try {
      const formData = new FormData();
      formData.append('barcode', barcodeSearch);
      formData.append('additional_stock', additionalStock);

      const response = await axios.post(`${API}/admin/products/barcode-stock`, formData, {
        headers: { 
          Authorization: `Bearer ${localStorage.getItem('adminToken')}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast({
        title: "Stock actualizado",
        description: response.data.message,
      });

      // Refresh products list
      adminFetchProducts();

      // Reset states
      setBarcodeSearch('');
      setBarcodeSearchResult(null);
      setAdditionalStock('');
      setShowBarcodeStockModal(false);

    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Error al actualizar el stock.",
        variant: "destructive"
      });
    }
  };

  const resetBarcodeSearch = () => {
    setBarcodeSearch('');
    setBarcodeSearchResult(null);
    setAdditionalStock('');
    setShowBarcodeStockModal(false);
  };

  const handleAddProduct = async (e) => {
    e.preventDefault();
    
    // For new products, at least one image is required
    const totalImages = (newProduct.images?.length || 0) + (newProduct.existingImages?.length || 0);
    if (!editingProduct && totalImages === 0) {
      toast({
        title: "Error",
        description: "Por favor selecciona al menos una imagen para el producto.",
        variant: "destructive"
      });
      return;
    }
    
    // Validate max 5 images
    if (totalImages > 5) {
      toast({
        title: "Error",
        description: "Máximo 5 imágenes permitidas por producto.",
        variant: "destructive"
      });
      return;
    }

    const formData = new FormData();
    formData.append('name', newProduct.name);
    formData.append('description', newProduct.description);
    formData.append('price', newProduct.price);
    if (newProduct.wholesale_price) {
      formData.append('wholesale_price', newProduct.wholesale_price);
    }
    formData.append('discount_percentage', newProduct.discount_percentage || 0);
    if (newProduct.discount_ends_at) {
      formData.append('discount_ends_at', newProduct.discount_ends_at);
    }
    formData.append('stock', newProduct.stock);
    formData.append('weight_kg', newProduct.weight_kg || '0');
    formData.append('weight_display', newProduct.weight_display || '0');
    formData.append('dimensions', newProduct.dimensions || '');
    formData.append('category', newProduct.category);
    formData.append('barcode', newProduct.barcode || '');
    formData.append('ingredients', newProduct.ingredients || '');
    
    // Add existing images to keep (for edit mode)
    if (editingProduct && newProduct.existingImages?.length > 0) {
      formData.append('keep_images', newProduct.existingImages.join(','));
    }
    
    // Add new images
    if (newProduct.images && newProduct.images.length > 0) {
      newProduct.images.forEach((image) => {
        formData.append('images', image);
      });
    } else if (!editingProduct) {
      // For new products without images array, this shouldn't happen but handle it
      toast({
        title: "Error",
        description: "Por favor selecciona al menos una imagen.",
        variant: "destructive"
      });
      return;
    }

    try {
      if (editingProduct) {
        // Update existing product
        await axios.put(`${API}/admin/products/${editingProduct.id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        toast({
          title: "Producto actualizado",
          description: "El producto ha sido actualizado exitosamente.",
        });
      } else {
        // Create new product
        await axios.post(`${API}/admin/products`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        toast({
          title: "Producto agregado",
          description: "El producto ha sido agregado exitosamente.",
        });
      }
      
      setIsAddingProduct(false);
      resetForm();
      adminFetchProducts();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || (editingProduct ? "Error al actualizar el producto." : "Error al agregar el producto.");
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
    }
  };

  const handleDeleteProduct = async (productId) => {
    try {
      console.log('🗑️ EXECUTING handleDeleteProduct for:', productId);
      console.log('🌐 Making API call to:', `${API}/admin/products/${productId}`);
      
      const response = await axios.delete(`${API}/admin/products/${productId}`);
      console.log('✅ Backend delete response status:', response.status);
      console.log('✅ Backend delete response data:', response.data);
      
      toast({
        title: "Producto eliminado",
        description: "El producto ha sido eliminado exitosamente.",
      });
      
      console.log('🔄 Forcing component refresh...');
      setRefreshKey(prev => prev + 1);
      
      console.log('📡 Fetching updated product list...');
      await adminFetchProducts();
      
      console.log('🎉 DELETE OPERATION COMPLETED SUCCESSFULLY');
      
    } catch (error) {
      console.error('❌ DELETE ERROR DETAILS:', error);
      console.error('❌ Error response:', error.response);
      console.error('❌ Error message:', error.message);
      
      toast({
        title: "Error",
        description: error.response?.data?.detail || error.message || "Error al eliminar el producto.",
        variant: "destructive"
      });
    }
  };

  const handleUpdateTracking = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const trackingNumber = formData.get('trackingNumber');

    try {
      await axios.put(`${API}/admin/orders/${trackingModal.orderId}/tracking`, {
        order_id: trackingModal.orderId,
        tracking_number: trackingNumber
      });
      
      toast({
        title: "Tracking actualizado",
        description: "Número de guía UPS actualizado y email enviado al cliente.",
      });
      
      setTrackingModal({ open: false, orderId: '', orderEmail: '' });
      fetchOrders();
    } catch (error) {
      toast({
        title: "Error",
        description: "Error al actualizar el número de guía.",
        variant: "destructive"
      });
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-pink-50 to-purple-50">
      {/* Header */}
      <header className="bg-white shadow-lg border-b-4 border-gradient-candy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_american-treats/artifacts/fa38658k_JUNTOS-2-PNG.png"
                alt="DI Candy Rush"
                className="h-12 w-auto"
              />
              <span className="text-2xl font-bold text-gray-800">Panel de Administración</span>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-gray-600">Bienvenido, {user?.username}</span>
              <Button 
                onClick={handleLogout}
                variant="outline"
                className="text-red-600 border-red-300 hover:bg-red-50"
              >
                Cerrar Sesión
              </Button>
              <Button 
                onClick={() => window.location.href = '/'}
                variant="outline"
                className="border-cyan-400 text-cyan-600 hover:bg-cyan-50"
              >
                Ver Tienda
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="products" data-testid="products-tab">
              <Package className="w-4 h-4 mr-2" />
              Productos
            </TabsTrigger>
            <TabsTrigger value="orders" data-testid="orders-tab">
              <Users className="w-4 h-4 mr-2" />
              Pedidos
            </TabsTrigger>
          </TabsList>

          <TabsContent value="products" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold">Gestión de Productos</h2>
              <Dialog open={isAddingProduct} onOpenChange={(open) => {
                setIsAddingProduct(open);
                if (!open) resetForm();
              }}>
                <DialogTrigger asChild>
                  <Button className="bg-gradient-to-r from-cyan-500 to-pink-500 hover:from-cyan-600 hover:to-pink-600" data-testid="add-product-button">
                    <Plus className="w-4 h-4 mr-2" />
                    Agregar Producto
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>{editingProduct ? 'Editar Producto' : 'Agregar Nuevo Producto'}</DialogTitle>
                    <DialogDescription>
                      {editingProduct ? 'Edita la información del producto.' : 'Agrega un nuevo producto dulce a tu tienda con peso y dimensiones para el cálculo de envío.'}
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleAddProduct} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="product-name">Nombre del Producto</Label>
                        <Input
                          id="product-name"
                          value={newProduct.name}
                          onChange={(e) => setNewProduct({...newProduct, name: e.target.value})}
                          required
                          data-testid="product-name-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="product-price">Precio (MXN)</Label>
                        <Input
                          id="product-price"
                          type="number"
                          step="0.01"
                          value={newProduct.price}
                          onChange={(e) => setNewProduct({...newProduct, price: e.target.value})}
                          required
                          data-testid="product-price-input"
                        />
                      </div>
                    </div>
                    
                    {/* Precio de Mayoreo */}
                    <div className="bg-gradient-to-r from-cyan-50 to-pink-50 p-4 rounded-lg border border-cyan-200">
                      <Label htmlFor="product-wholesale-price" className="flex items-center space-x-2">
                        <span>💼 Precio Mayoreo (MXN)</span>
                        <span className="text-xs text-gray-500 font-normal">- Se aplica en compras ≥ $3,000</span>
                      </Label>
                      <Input
                        id="product-wholesale-price"
                        type="number"
                        step="0.01"
                        value={newProduct.wholesale_price}
                        onChange={(e) => setNewProduct({...newProduct, wholesale_price: e.target.value})}
                        placeholder="Dejar vacío si no aplica"
                        className="mt-2"
                        data-testid="product-wholesale-price-input"
                      />
                      {newProduct.price && newProduct.wholesale_price && (
                        <p className="text-xs text-green-600 mt-1">
                          Descuento: {((1 - parseFloat(newProduct.wholesale_price) / parseFloat(newProduct.price)) * 100).toFixed(0)}% menos que el precio regular
                        </p>
                      )}
                    </div>
                    
                    {/* Ofertas y Descuentos */}
                    <div className="bg-gradient-to-r from-orange-50 to-red-50 p-4 rounded-lg border border-orange-200">
                      <Label className="flex items-center space-x-2 mb-3">
                        <span>🏷️ Oferta / Descuento</span>
                        <span className="text-xs text-gray-500 font-normal">- Descuento temporal con temporizador</span>
                      </Label>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="discount-percentage" className="text-sm">Porcentaje de Descuento</Label>
                          <select
                            id="discount-percentage"
                            value={newProduct.discount_percentage}
                            onChange={(e) => setNewProduct({...newProduct, discount_percentage: parseInt(e.target.value)})}
                            className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                          >
                            <option value={0}>Sin descuento</option>
                            <option value={5}>5% de descuento</option>
                            <option value={10}>10% de descuento</option>
                            <option value={15}>15% de descuento</option>
                            <option value={20}>20% de descuento</option>
                            <option value={25}>25% de descuento</option>
                          </select>
                        </div>
                        
                        <div>
                          <Label htmlFor="discount-ends" className="text-sm">Termina el</Label>
                          <Input
                            id="discount-ends"
                            type="datetime-local"
                            value={newProduct.discount_ends_at}
                            onChange={(e) => setNewProduct({...newProduct, discount_ends_at: e.target.value})}
                            className="mt-1"
                            disabled={newProduct.discount_percentage === 0}
                          />
                        </div>
                      </div>
                      
                      {newProduct.price && newProduct.discount_percentage > 0 && (
                        <div className="mt-3 p-2 bg-white rounded border border-orange-200">
                          <p className="text-sm">
                            <span className="text-gray-600">Precio original:</span> 
                            <span className="line-through ml-1">${parseFloat(newProduct.price).toFixed(2)}</span>
                          </p>
                          <p className="text-lg font-bold text-orange-600">
                            Precio con oferta: ${(parseFloat(newProduct.price) * (1 - newProduct.discount_percentage / 100)).toFixed(2)} MXN
                          </p>
                          {newProduct.discount_ends_at && (
                            <p className="text-xs text-gray-500 mt-1">
                              ⏰ La oferta termina: {new Date(newProduct.discount_ends_at).toLocaleString('es-MX')}
                            </p>
                          )}
                        </div>
                      )}
                      
                      {newProduct.discount_percentage > 0 && !newProduct.discount_ends_at && (
                        <p className="text-xs text-orange-600 mt-2">
                          ⚠️ Configura una fecha de fin para que el descuento se quite automáticamente
                        </p>
                      )}
                    </div>
                    
                    <div>
                      <Label htmlFor="product-description">Descripción</Label>
                      <Textarea
                        id="product-description"
                        value={newProduct.description}
                        onChange={(e) => setNewProduct({...newProduct, description: e.target.value})}
                        required
                        data-testid="product-description-input"
                      />
                    </div>
                    
                    <div className="grid grid-cols-4 gap-4">
                      <div>
                        <Label htmlFor="product-category">Categoría</Label>
                        <select 
                          value={newProduct.category} 
                          onChange={(e) => setNewProduct({...newProduct, category: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-cyan-500 focus:border-cyan-500"
                        >
                          {categories.map((category) => (
                            <option key={category} value={category}>
                              {category}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label htmlFor="product-stock">Stock</Label>
                        <Input
                          id="product-stock"
                          type="number"
                          value={newProduct.stock}
                          onChange={(e) => setNewProduct({...newProduct, stock: e.target.value})}
                          required
                          data-testid="product-stock-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="product-weight-kg">Peso para envío (kg)</Label>
                        <Input
                          id="product-weight-kg"
                          type="number"
                          step="0.001"
                          value={newProduct.weight_kg}
                          onChange={(e) => setNewProduct({...newProduct, weight_kg: e.target.value})}
                          placeholder="0.250"
                          data-testid="product-weight-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="product-weight-display">Peso mostrado (g/ml según categoría)</Label>
                        <Input
                          id="product-weight-display"
                          type="number"
                          step="0.1"
                          value={newProduct.weight_display}
                          onChange={(e) => setNewProduct({...newProduct, weight_display: e.target.value})}
                          placeholder="250"
                          data-testid="product-weight-display-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="product-dimensions">Dimensiones</Label>
                        <Input
                          id="product-dimensions"
                          value={newProduct.dimensions}
                          onChange={(e) => setNewProduct({...newProduct, dimensions: e.target.value})}
                          placeholder="10x5x3 cm"
                          data-testid="product-dimensions-input"
                        />
                      </div>
                    </div>
                    
                    {/* Campo de Ingredientes */}
                    <div>
                      <Label htmlFor="product-ingredients">Ingredientes</Label>
                      <Textarea
                        id="product-ingredients"
                        value={newProduct.ingredients}
                        onChange={(e) => setNewProduct({...newProduct, ingredients: e.target.value})}
                        placeholder="Lista de ingredientes del producto..."
                        className="min-h-[100px]"
                        data-testid="product-ingredients-input"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Ingresa los ingredientes tal como aparecen en el empaque del producto.
                      </p>
                    </div>
                    
                    <div>
                      <Label htmlFor="product-images">
                        Imágenes del Producto (máximo 5) {editingProduct && '- Las nuevas imágenes se agregarán a las existentes'}
                      </Label>
                      
                      {/* Show existing images when editing */}
                      {editingProduct && newProduct.existingImages?.length > 0 && (
                        <div className="mb-3">
                          <p className="text-sm text-gray-600 mb-2">Imágenes actuales ({newProduct.existingImages.length}/5):</p>
                          <div className="flex flex-wrap gap-2">
                            {newProduct.existingImages.map((url, index) => (
                              <div key={`existing-${url}`} className="relative group">
                                <img 
                                  src={getImageUrl(url)} 
                                  alt={`Imagen ${index + 1}`}
                                  className="w-20 h-20 object-cover rounded border"
                                />
                                <button
                                  type="button"
                                  onClick={() => {
                                    const updated = newProduct.existingImages.filter((_, i) => i !== index);
                                    setNewProduct({...newProduct, existingImages: updated});
                                  }}
                                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  ×
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Show preview of new images */}
                      {newProduct.images?.length > 0 && (
                        <div className="mb-3">
                          <p className="text-sm text-gray-600 mb-2">Nuevas imágenes ({newProduct.images.length}):</p>
                          <div className="flex flex-wrap gap-2">
                            {newProduct.images.map((file, index) => (
                              <div key={`new-${file.name}-${file.size}-${file.lastModified}`} className="relative group">
                                <img 
                                  src={URL.createObjectURL(file)} 
                                  alt={`Nueva imagen ${index + 1}`}
                                  className="w-20 h-20 object-cover rounded border border-green-500"
                                />
                                <button
                                  type="button"
                                  onClick={() => {
                                    const updated = newProduct.images.filter((_, i) => i !== index);
                                    setNewProduct({...newProduct, images: updated});
                                  }}
                                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  ×
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* File input for new images */}
                      <Input
                        id="product-images"
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={(e) => {
                          const files = Array.from(e.target.files || []);
                          const currentTotal = (newProduct.existingImages?.length || 0) + (newProduct.images?.length || 0);
                          const maxNew = 5 - currentTotal;
                          
                          if (files.length > maxNew) {
                            toast({
                              title: "Límite de imágenes",
                              description: `Solo puedes agregar ${maxNew} imagen(es) más. Máximo 5 en total.`,
                              variant: "destructive"
                            });
                            const limitedFiles = files.slice(0, maxNew);
                            setNewProduct({...newProduct, images: [...(newProduct.images || []), ...limitedFiles]});
                          } else {
                            setNewProduct({...newProduct, images: [...(newProduct.images || []), ...files]});
                          }
                          e.target.value = ''; // Reset input to allow selecting same files again
                        }}
                        disabled={(newProduct.existingImages?.length || 0) + (newProduct.images?.length || 0) >= 5}
                        data-testid="product-images-input"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Total: {(newProduct.existingImages?.length || 0) + (newProduct.images?.length || 0)}/5 imágenes
                      </p>
                    </div>
                    <DialogFooter>
                      <Button type="submit" data-testid="save-product-button">
                        {editingProduct ? 'Actualizar Producto' : 'Guardar Producto'}
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {/* Barcode Quick Stock - Search & Add Stock by Barcode */}
            <Card className="border-2 border-cyan-200 bg-gradient-to-r from-cyan-50 to-pink-50" data-testid="barcode-search-card">
              <CardContent className="p-4">
                <div className="flex items-center space-x-2 mb-3">
                  <Barcode className="w-5 h-5 text-cyan-600" />
                  <h3 className="text-lg font-semibold text-gray-800">Buscar y Reabastecer por Código de Barras</h3>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Input
                    type="text"
                    placeholder="Ingresa o escanea el código de barras..."
                    value={barcodeSearch}
                    onChange={(e) => setBarcodeSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        searchByBarcode();
                      }
                    }}
                    className="flex-1 bg-white"
                    data-testid="barcode-search-input"
                  />
                  <Button
                    onClick={searchByBarcode}
                    disabled={isSearchingBarcode || !barcodeSearch.trim()}
                    className="bg-gradient-to-r from-cyan-500 to-pink-500 hover:from-cyan-600 hover:to-pink-600"
                    data-testid="barcode-search-button"
                  >
                    <Search className="w-4 h-4 mr-2" />
                    {isSearchingBarcode ? 'Buscando...' : 'Buscar'}
                  </Button>
                  {(barcodeSearch || barcodeSearchResult) && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        setBarcodeSearch('');
                        setBarcodeSearchResult(null);
                        setAdditionalStock('');
                      }}
                      data-testid="barcode-clear-button"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>

                {/* Search Result */}
                {barcodeSearchResult && barcodeSearchResult.found && barcodeSearchResult.product && (
                  <div className="mt-4 p-4 bg-white rounded-lg border border-green-200" data-testid="barcode-search-result">
                    <div className="flex items-start space-x-4">
                      {barcodeSearchResult.product.image_url && (
                        <img
                          src={getImageUrl(barcodeSearchResult.product.image_url)}
                          alt={barcodeSearchResult.product.name}
                          className="w-20 h-20 object-contain rounded bg-gray-50"
                        />
                      )}
                      <div className="flex-1">
                        <h4 className="font-bold text-gray-900">{barcodeSearchResult.product.name}</h4>
                        <p className="text-sm text-gray-600 line-clamp-2">{barcodeSearchResult.product.description}</p>
                        <div className="flex flex-wrap gap-3 mt-2 text-sm">
                          <span className="text-pink-600 font-semibold">
                            ${parseFloat(barcodeSearchResult.product.price).toFixed(2)} MXN
                          </span>
                          <Badge className={barcodeSearchResult.product.stock > 0 ? 'bg-green-500' : 'bg-red-500'}>
                            Stock actual: {barcodeSearchResult.product.stock}
                          </Badge>
                          <span className="text-gray-500">Código: {barcodeSearchResult.product.barcode}</span>
                        </div>
                      </div>
                      <Button
                        onClick={() => setShowBarcodeStockModal(true)}
                        className="bg-green-600 hover:bg-green-700 text-white"
                        data-testid="barcode-add-stock-button"
                      >
                        <PackagePlus className="w-4 h-4 mr-2" />
                        Agregar Stock
                      </Button>
                    </div>
                  </div>
                )}

                {/* Not Found Message */}
                {barcodeSearchResult && !barcodeSearchResult.found && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-testid="barcode-not-found">
                    {barcodeSearchResult.message || 'Producto no encontrado con este código de barras'}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Modal: Add Stock by Barcode */}
            <Dialog open={showBarcodeStockModal} onOpenChange={setShowBarcodeStockModal}>
              <DialogContent data-testid="barcode-stock-modal">
                <DialogHeader>
                  <DialogTitle>Agregar Stock</DialogTitle>
                  <DialogDescription>
                    {barcodeSearchResult?.product?.name && (
                      <>Producto: <span className="font-semibold">{barcodeSearchResult.product.name}</span></>
                    )}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="current-stock">Stock actual</Label>
                    <Input
                      id="current-stock"
                      type="number"
                      value={barcodeSearchResult?.product?.stock ?? 0}
                      disabled
                      className="bg-gray-100"
                    />
                  </div>
                  <div>
                    <Label htmlFor="additional-stock">Cantidad a agregar</Label>
                    <Input
                      id="additional-stock"
                      type="number"
                      min="1"
                      placeholder="Ej: 50"
                      value={additionalStock}
                      onChange={(e) => setAdditionalStock(e.target.value)}
                      autoFocus
                      data-testid="additional-stock-input"
                    />
                  </div>
                  {additionalStock && parseInt(additionalStock) > 0 && (
                    <div className="p-3 bg-green-50 rounded text-sm text-green-700">
                      Nuevo stock: <span className="font-bold">
                        {(barcodeSearchResult?.product?.stock || 0) + parseInt(additionalStock)}
                      </span> unidades
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowBarcodeStockModal(false)}>
                    Cancelar
                  </Button>
                  <Button
                    onClick={addStockByBarcode}
                    disabled={!additionalStock || parseInt(additionalStock) < 1}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="confirm-add-stock-button"
                  >
                    Confirmar
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" key={refreshKey}>
              {products.map((product) => (
                <Card key={`${refreshKey}-${product.id}`} className="group hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 bg-white border-2 border-transparent hover:border-cyan-200">
                  <CardHeader className="p-0 relative">
                    <div className="relative overflow-hidden rounded-t-lg">
                      <img 
                        src={getImageUrl(product.image_url)}
                        alt={product.name}
                        className="w-full h-48 object-contain group-hover:scale-105 transition-transform duration-300 bg-gray-50"
                      />
                      {/* Category badge */}
                      <Badge className="absolute top-3 left-3 bg-cyan-500 text-white">
                        {product.category}
                      </Badge>
                      {/* Stock badge */}
                      <Badge 
                        className={`absolute top-3 right-3 ${product.stock > 0 ? 'bg-green-500' : 'bg-red-500'} text-white`}
                      >
                        {product.stock > 0 ? `${product.stock} en stock` : 'Agotado'}
                      </Badge>
                      {/* Show image count badge if multiple images */}
                      {product.image_urls && product.image_urls.length > 1 && (
                        <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded-full">
                          📷 {product.image_urls.length} fotos
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="p-4">
                    <CardTitle className="text-lg font-bold text-gray-900 mb-2 line-clamp-1">
                      {product.name}
                    </CardTitle>
                    <CardDescription className="text-gray-600 mb-3 line-clamp-2 min-h-[40px]">
                      {product.description}
                    </CardDescription>
                    
                    {/* Weight and dimensions info */}
                    <div className="flex items-center space-x-4 mb-3 text-sm text-gray-500">
                      {product.weight_display > 0 && (
                        <div className="flex items-center">
                          <Scale className="w-3 h-3 mr-1" />
                          {product.weight_display}{product.weight_unit || 'g'}
                        </div>
                      )}
                      {product.dimensions && (
                        <div className="flex items-center">
                          <Package className="w-3 h-3 mr-1" />
                          {product.dimensions}
                        </div>
                      )}
                    </div>
                    
                    {/* Prices section */}
                    <div className="space-y-1 mb-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-500">Precio:</span>
                        <span className="text-xl font-bold text-pink-600">
                          ${parseFloat(product.price).toFixed(2)} MXN
                        </span>
                      </div>
                      {product.wholesale_price && (
                        <div className="flex justify-between items-center bg-green-50 px-2 py-1 rounded">
                          <span className="text-sm text-green-600">💼 Mayoreo:</span>
                          <span className="text-lg font-semibold text-green-600">
                            ${parseFloat(product.wholesale_price).toFixed(2)} MXN
                          </span>
                        </div>
                      )}
                    </div>
                    
                    {/* Barcode if exists */}
                    {product.barcode && (
                      <div className="text-xs text-gray-400 mb-2">
                        Código: {product.barcode}
                      </div>
                    )}
                  </CardContent>
                  <CardFooter className="flex space-x-2 p-4 pt-0">
                    <Button 
                      variant="outline"
                      size="sm"
                      className="flex-1 hover:bg-cyan-50 hover:border-cyan-300"
                      onClick={() => handleEditProduct(product.id)}
                      data-testid={`edit-product-${product.id}`}
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Editar
                    </Button>
                    <Button 
                      variant="destructive"
                      size="sm"
                      className="flex-1"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleDeleteProduct(product.id);
                      }}
                      data-testid={`delete-product-${product.id}`}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Eliminar
                    </Button>
                  </CardFooter>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="orders">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold">Gestión de Pedidos</h2>
              <Button
                variant="destructive"
                size="sm"
                onClick={async () => {
                  if (window.confirm('⚠️ ¿Estás seguro de eliminar TODOS los pedidos? Esta acción no se puede deshacer.')) {
                    try {
                      const token = localStorage.getItem('adminToken');
                      if (!token) {
                        toast({
                          title: "Error",
                          description: "No hay sesión activa. Por favor inicia sesión de nuevo.",
                          variant: "destructive"
                        });
                        return;
                      }
                      const response = await axios({
                        method: 'DELETE',
                        url: `${API}/admin/orders/clear-all`,
                        headers: { 
                          'Authorization': `Bearer ${token}`,
                          'Content-Type': 'application/json'
                        }
                      });
                      toast({
                        title: "Pedidos eliminados",
                        description: response.data.message,
                      });
                      fetchOrders();
                    } catch (error) {
                      console.error('Error deleting orders:', error);
                      toast({
                        title: "Error",
                        description: error.response?.data?.detail || "Error al eliminar los pedidos.",
                        variant: "destructive"
                      });
                    }
                  }
                }}
                data-testid="clear-all-orders-btn"
              >
                🗑️ Eliminar Todos los Pedidos
              </Button>
            </div>
            
            {/* Order Filters */}
            <div className="flex flex-wrap gap-2 mb-6">
              <Button
                variant={orderFilter === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setOrderFilter('all')}
                className={orderFilter === 'all' ? 'bg-gradient-to-r from-cyan-500 to-pink-500' : ''}
              >
                Todos ({orders.length})
              </Button>
              <Button
                variant={orderFilter === 'pending' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setOrderFilter('pending')}
                className={orderFilter === 'pending' ? 'bg-yellow-500 hover:bg-yellow-600' : ''}
              >
                ⏳ Pendientes ({orders.filter(o => o.payment_status !== 'approved' && !o.ups_tracking_number).length})
              </Button>
              <Button
                variant={orderFilter === 'paid' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setOrderFilter('paid')}
                className={orderFilter === 'paid' ? 'bg-green-500 hover:bg-green-600' : ''}
              >
                ✅ Pagados ({orders.filter(o => (o.payment_status === 'approved' || o.payment_method === 'test_payment') && !o.ups_tracking_number).length})
              </Button>
              <Button
                variant={orderFilter === 'shipped' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setOrderFilter('shipped')}
                className={orderFilter === 'shipped' ? 'bg-blue-500 hover:bg-blue-600' : ''}
              >
                🚚 Enviados ({orders.filter(o => o.ups_tracking_number).length})
              </Button>
            </div>
            
            <div className="space-y-4">
              {orders
                .filter(order => {
                  if (orderFilter === 'all') return true;
                  if (orderFilter === 'pending') return order.payment_status !== 'approved' && !order.ups_tracking_number;
                  if (orderFilter === 'paid') return (order.payment_status === 'approved' || order.payment_method === 'test_payment') && !order.ups_tracking_number;
                  if (orderFilter === 'shipped') return order.ups_tracking_number;
                  return true;
                })
                .map((order) => (
                <Card key={order.id} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-lg">Pedido #{order.id.slice(-8)}</CardTitle>
                        <CardDescription>
                          {order.customer_name} - {order.customer_email}
                        </CardDescription>
                        <p className="text-sm text-gray-500 flex items-center mt-1">
                          <Scale className="w-3 h-3 mr-1" />
                          Peso total: {order.total_weight?.toFixed(1) || 0}kg
                        </p>
                      </div>
                      <div className="text-right">
                        {/* Show status based on order progress */}
                        {order.ups_tracking_number ? (
                          <Badge className="bg-blue-500">
                            🚚 Enviado
                          </Badge>
                        ) : (order.payment_status === 'approved' || order.payment_method === 'test_payment') ? (
                          <Badge className="bg-green-500">
                            ✅ Pagado
                          </Badge>
                        ) : (
                          <Badge className="bg-yellow-500">
                            ⏳ Pendiente
                          </Badge>
                        )}
                        <p className="text-sm text-gray-500 mt-1">
                          {new Date(order.created_at).toLocaleDateString('es-MX')}
                        </p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {order.items.map((item) => (
                        <div key={item.product_id} className="flex justify-between text-sm">
                          <span>{item.name} x {item.quantity}</span>
                          <span>${(parseFloat(item.price) * item.quantity).toFixed(2)} MXN</span>
                        </div>
                      ))}
                      <Separator />
                      <div className="flex justify-between text-sm">
                        <span>Subtotal:</span>
                        <span>${parseFloat(order.total_amount).toFixed(2)} MXN</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>Envío:</span>
                        <span>${parseFloat(order.shipping_cost || 0).toFixed(2)} MXN</span>
                      </div>
                      <div className="flex justify-between font-semibold">
                        <span>Total:</span>
                        <span>${parseFloat(order.grand_total || order.total_amount).toFixed(2)} MXN</span>
                      </div>
                      
                      {order.ups_tracking_number && (
                        <div className="mt-2 p-2 bg-green-50 rounded flex items-center">
                          <Truck className="w-4 h-4 mr-2 text-green-600" />
                          <span className="text-sm text-green-700">
                            UPS Tracking: {order.ups_tracking_number}
                          </span>
                        </div>
                      )}
                    </div>
                    
                    <div className="mt-4 flex flex-wrap gap-2">
                      {!order.ups_tracking_number && (order.payment_status === 'approved' || order.payment_method === 'test_payment') && (
                        <Button
                          size="sm"
                          onClick={() => setTrackingModal({ 
                            open: true, 
                            orderId: order.id, 
                            orderEmail: order.customer_email 
                          })}
                          className="bg-gradient-to-r from-cyan-500 to-pink-500 hover:from-cyan-600 hover:to-pink-600"
                        >
                          <Truck className="w-4 h-4 mr-2" />
                          Agregar Guía UPS
                        </Button>
                      )}

                      {order.payment_status !== 'approved' && (order.payment_method === 'mercadopago_wallet' || order.payment_method === 'mercadopago_card') && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-green-500 text-green-700 hover:bg-green-50"
                          onClick={async () => {
                            try {
                              const response = await axios.post(
                                `${API}/admin/orders/${order.id}/sync-payment`,
                                {},
                                { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } }
                              );
                              const d = response.data;
                              if (d.found_payments === 0) {
                                toast({
                                  title: "Sin pagos en MP",
                                  description: "Mercado Pago no tiene registros de pago para este pedido. El cliente probablemente no completó el checkout.",
                                });
                              } else {
                                toast({
                                  title: `Estado MP: ${d.mp_status}`,
                                  description: `Pedido actualizado a "${d.new_payment_status}". ${d.new_payment_status === 'approved' ? '¡Ya puedes preparar el envío!' : 'Aún no está aprobado.'}`,
                                });
                                fetchOrders();
                              }
                            } catch (err) {
                              toast({
                                title: "Error",
                                description: err.response?.data?.detail || "No se pudo consultar Mercado Pago",
                                variant: "destructive"
                              });
                            }
                          }}
                          data-testid={`sync-payment-${order.id}`}
                        >
                          🔄 Verificar pago con MP
                        </Button>
                      )}
                      
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={async () => {
                          try {
                            const response = await axios.get(`${API}/emails/preview/${order.id}`);
                            // Safer than document.write: serve the HTML as a Blob URL.
                            // The new window renders the blob in its own origin context,
                            // avoiding direct DOM injection into the current page.
                            const blob = new Blob([response.data.content], { type: 'text/html; charset=utf-8' });
                            const blobUrl = URL.createObjectURL(blob);
                            const newWindow = window.open(blobUrl, '_blank', 'noopener,noreferrer');
                            // Revoke after a short delay so the new window has time to load
                            setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
                            if (!newWindow) {
                              toast({
                                title: "Ventana bloqueada",
                                description: "Permite ventanas emergentes para ver la vista previa.",
                                variant: "destructive"
                              });
                            }
                          } catch (error) {
                            toast({
                              title: "Error",
                              description: "No se pudo cargar la vista previa del email",
                              variant: "destructive"
                            });
                          }
                        }}
                      >
                        <Mail className="w-4 h-4 mr-2" />
                        Vista Previa Email
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* UPS Tracking Modal */}
      <Dialog open={trackingModal.open} onOpenChange={(open) => setTrackingModal({...trackingModal, open})}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Agregar Número de Guía UPS</DialogTitle>
            <DialogDescription>
              Ingresa el número de guía UPS para el pedido #{trackingModal.orderId.slice(-8)}. 
              Se enviará un email de notificación a {trackingModal.orderEmail}.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateTracking}>
            <div className="space-y-4">
              <div>
                <Label htmlFor="trackingNumber">Número de Guía UPS</Label>
                <Input
                  id="trackingNumber"
                  name="trackingNumber"
                  placeholder="1Z123E456789012345"
                  required
                  data-testid="tracking-number-input"
                />
              </div>
            </div>
            <DialogFooter className="mt-4">
              <Button type="submit" data-testid="save-tracking-button">
                Guardar y Enviar Email
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Componente de redirección para modo catálogo
const CatalogModeRedirect = () => {
  const navigate = useNavigate();
  useEffect(() => {
    navigate('/');
  }, [navigate]);
  return null;
};

// Main App Component
function App() {
  // Initialize Mercado Pago
  useEffect(() => {
    const publicKey = process.env.REACT_APP_MERCADOPAGO_PUBLIC_KEY;
    if (publicKey) {
      try {
        initMercadoPago(publicKey);
        console.log('✅ Mercado Pago SDK initialized successfully');
      } catch (error) {
        console.error('❌ Failed to initialize Mercado Pago SDK:', error);
      }
    } else {
      console.warn('⚠️ Mercado Pago public key not found in environment');
    }
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/" element={<Homepage />} />
            {/* Rutas de compra - Solo disponibles si no está en modo catálogo */}
            <Route path="/cart" element={CATALOG_MODE ? <CatalogModeRedirect /> : <ShoppingCartPage />} />
            <Route path="/checkout" element={CATALOG_MODE ? <CatalogModeRedirect /> : <Checkout />} />
            <Route path="/order-confirmation/:orderId" element={<OrderConfirmation />} />
            <Route path="/wholesale" element={<WholesaleContact />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-conditions" element={<TermsConditions />} />
            <Route path="/admin/login" element={<LoginPage />} />
            <Route 
              path="/admin" 
              element={
                <ProtectedRoute>
                  <AdminPanel />
                </ProtectedRoute>
              } 
            />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
      <Toaster />
      <CookieBanner />
    </div>
  );
}

export default App;